import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Mail, Phone, MapPin, Menu, X, Building, Users, Star, ShieldCheck, ChevronLeft, ChevronRight, ArrowRight, Instagram, Facebook, Award, TrendingUp, Sparkles, Gem, Plus, Minus, FileText, Wrench, Lightbulb, Ruler, Home, Briefcase, Layers, ShoppingBag, Maximize2 } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Admin from './components/Admin';
import { useAuth } from './hooks/useAuth';
import { useProducts } from './hooks/useProducts';

// Helper to construct Tailwind classes
const cl = (...classes) => classes.filter(Boolean).join(' ');

// Cloudinary optimization helper
const getOptimizedImg = (url, width) => {
    if (!url) return url;
    try {
        if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
            const transform = `f_auto,q_auto${width ? ',w_'+width : ''}`;
            return url.replace('/upload/', `/upload/${transform}/`);
        }
    } catch {}
    return url;
};

// Custom Hook for Scroll Animations using Intersection Observer
const useScrollAnimation = (options = { threshold: 0.1, triggerOnce: true }) => {
    const [ref, setRef] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                if (ref && options.triggerOnce) {
                    observer.unobserve(ref);
                }
            }
        }, options);

        if (ref) {
            observer.observe(ref);
        }

        return () => {
            if (ref) {
                observer.unobserve(ref);
            }
        };
    }, [ref, options]);

    const animationClasses = isVisible
        ? 'opacity-100 translate-y-0'
        : 'opacity-0 translate-y-10';

    return { setRef, animationClasses };
};

// Animated Component Wrapper
const Animated = ({ children, as = 'div', className = '', ...props }) => {
    const { setRef, animationClasses } = useScrollAnimation();
    const Component = as;
    return (
        <Component ref={setRef} className={cl('transition-all duration-700 ease-out', animationClasses, className)} {...props}>
            {children}
        </Component>
    );
};

// Mock Data - Updated with new contact info and product details
const navLinks = ['Início', 'Sobre', 'Produtos', 'Serviços', 'Portfólio', 'Corporativo', 'Contato'];
const legalLinks = ['Política de Privacidade', 'Termos de Uso', 'Aviso Legal'];

const differentials = [
  { icon: <Building size={32} className="text-[#D67441]" />, title: 'Combo Completo', text: 'Projeto, material, instalação e garantia em um só lugar.' },
  { icon: <Users size={32} className="text-[#D67441]" />, title: 'Mão de Obra Própria', text: 'Equipe qualificada e treinada para a instalação perfeita.' },
  { icon: <Star size={32} className="text-[#D67441]" />, title: '40+ Anos de Experiência', text: 'Tradição familiar e expertise reconhecida no mercado.' },
  { icon: <ShieldCheck size={32} className="text-[#D67441]" />, title: 'Garantia Total', text: 'Cobertura completa em produtos e serviços para sua segurança.' },
];
const products = [
  { name: 'Pisos Vinílicos', img: 'https://tarkett.com.br/blog/wp-content/uploads/2024/12/Piso-Vinilico-Cor-Tulipa-Linha-Injoy-Tarkett-alta-scaled.jpg' },
  { name: 'Pisos Laminados', img: 'https://static.jmdecoracoes.com.br/public/atacadomachado/imagens/produtos/piso-laminado-click-evidence-217x1357x7mm-carvalho-canela-eucafloor-2-36m2-2389.jpg' },
  { name: 'Carpetes', img: 'https://pisoscia.com.br/wp-content/uploads/2018/08/p19i7ket5ppq21qng1tfld2mh4u41-8fac0120fa1cb1424815360022792724-640-0.jpg' },
  { name: 'Forros', img: 'https://www.jomag.com.br/images/artigos/galeria-interna/voce-conhece-os-5-principais-tipos-de-forros-no-abc/voce-conhece-os-5-principais-tipos-de-forros-no-abc-1.jpg' },
  { name: 'Rodapés', img: 'https://formacodecor.com.br/cdn/shop/files/80_1fffccc4-d304-4c24-9efb-b658003b3d56.png?v=1731443081&width=1445' },
];
const partnersData = [
    { main: 'DURAFLOOR', sub: 'DURATEX' },
    { main: 'MASSA AUTONIVELANTE', sub: 'PROTEC' },
    { main: 'BARRAMENTO DE VAPOR', sub: 'HDFLEX' },
    { main: 'COLAS ESPECIAIS', sub: 'SILVA SELANTES' },
    { main: 'EUCAFLOOR', sub: 'EUCATEX' },
    { main: 'DKFLOOR PIX', sub: 'RIVATTI' },
    { main: 'VYNILFORTE', sub: 'CARMELO FIOR' },
    { main: 'QUICK STEP', sub: 'UNILIN' },
    { main: 'CHÂTEAU', sub: 'ARQUITECH' },
    { main: 'HOSPITALAR', sub: 'TARKETT' }
];
const testimonials = [
  { text: 'O modelo Combo da Dsanzo facilitou muito a minha reforma. Atendimento e qualidade impecáveis do início ao fim do projeto!', author: 'Mariana L.', role: 'Cliente Residencial', rating: 5, img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=256&q=80' },
  { text: 'São parceiros confiáveis há anos. Os produtos são de alta qualidade e o suporte técnico que oferecem é excepcional.', author: 'Carlos S.', role: 'Instalador Profissional', rating: 5, img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=256&q=80' },
  { text: 'A garantia total e o profissionalismo da equipe me deram a segurança que eu precisava para investir. O resultado superou as expectativas.', author: 'Juliana R.', role: 'Arquiteta', rating: 5, img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=256&q=80' },
];
const timelineData = [
    { year: 1980, title: 'Fundação', text: 'Levy e Úbya fundam o Grupo Dsanzo em Indaiatuba, SP, com o sonho de oferecer soluções completas em pisos e revestimentos.', icon: <Sparkles/> },
    { year: 1995, title: 'Expansão dos Produtos', text: 'Ampliação da linha de produtos incluindo pisos vinílicos e laminados, acompanhando as tendências do mercado.', icon: <Gem/> },
    { year: 2005, title: 'Criação das Marcas', text: 'Lançamento das marcas DK DKOR (varejo) e Levs Revest (atacado), especializando o atendimento para diferentes públicos.', icon: <Award/> },
    { year: 2015, title: 'Modelo Combo', text: 'Desenvolvimento do exclusivo modelo Combo: Projeto + Material + Instalação + Garantia, tudo em um só lugar.', icon: <ShieldCheck/> },
    { year: 2024, title: 'Crescimento de 50%', text: 'Consolidação como referência no mercado com crescimento significativo e expansão para toda a região Sudeste.', icon: <TrendingUp/> },
];
const faqData = [
    { question: 'O que está incluído no Combo Dsanzo?', answer: 'O Combo Dsanzo é nossa solução completa que inclui: consultoria e projeto personalizado, fornecimento de todos os materiais (pisos, rodapés, cola, etc.), mão de obra própria e especializada para instalação, e a garantia total sobre produtos e serviços. É a sua reforma do início ao fim, com um único parceiro.' },
    { question: 'Qual é a garantia oferecida pela Dsanzo?', answer: 'Oferecemos garantia total, que cobre tanto os defeitos de fabricação dos produtos (seguindo as diretrizes dos fabricantes parceiros) quanto a nossa mão de obra de instalação. Isso significa mais segurança e tranquilidade para você.' },
    { question: 'Posso comprar apenas os materiais, sem a instalação?', answer: 'Sim! Através da nossa marca de atacado, a Levs Revest, você pode adquirir apenas os materiais. Oferecemos um vasto catálogo de produtos para instaladores, lojistas, arquitetos e para quem prefere o modelo "faça você mesmo".' },
    { question: 'Como funciona o treinamento para instaladores?', answer: 'Nosso treinamento gratuito para instaladores é um dos nossos grandes diferenciais. Conduzido pelo próprio Levy, com sua vasta experiência prática, o treinamento aborda as melhores técnicas de instalação, uso de ferramentas e segredos para um acabamento perfeito. Entre em contato para saber as próximas datas.' },
];
const productList = [
    { name: 'Piso Vinílico Colado', type: 'vinilico', brand: 'Vinilfloor, PIX', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Vinílico+Colado', thickness: '2mm', feature: 'Antiderrapante e econômico', environments: 'Residencial, Escritórios', description: 'Solução econômica e versátil, ideal para reformas rápidas com excelente custo-benefício.' },
    { name: 'Piso Vinílico LVT Premium', type: 'vinilico', brand: 'Eucafloor, PIX, Tarkett', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Vinílico+Premium', thickness: '4mm', feature: 'Resistente à água (Click)', environments: 'Residencial, Comercial, Hospitais', description: 'Linha premium com tecnologia LVT, ideal para áreas de alto tráfego com acabamento fiel à madeira natural.' },
    { name: 'Piso Laminado Flutuante AC3', type: 'laminado', brand: 'Eucafloor, Durafloor', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Laminado+AC3', thickness: '7mm', feature: 'Instalação rápida e fácil', environments: 'Residencial', description: 'Perfeito para ambientes residenciais, com instalação rápida e fácil manutenção.' },
    { name: 'Piso Laminado Click AC4', type: 'laminado', brand: 'Durafloor, Eucafloor', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Laminado+AC4', thickness: '8mm', feature: 'Alta resistência para uso comercial', environments: 'Residencial, Comercial Leve', description: 'Resistência AC4 para uso comercial. Textura realista que imita perfeitamente a madeira natural.' },
    { name: 'Carpete Residencial Plush', type: 'carpete', brand: 'Belmont, Beaulieu', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Carpete+Plush', thickness: 'Nylon Antialérgico', feature: 'Textura macia e antialérgica', environments: 'Quartos, Salas', description: 'Máximo conforto para quartos e salas. Textura macia e propriedades antialérgicas.' },
    { name: 'Carpete Comercial Loop', type: 'carpete', brand: 'Tarkett, Interface', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Carpete+Loop', thickness: 'Polipropileno', feature: 'Resistência superior para alto tráfego', environments: 'Escritórios, Hotéis, Lojas', description: 'Resistência superior para áreas de alto tráfego. Ideal para escritórios e espaços comerciais.' },
    { name: 'Forro Gesso Acartonado', type: 'forro', brand: 'Knauf, Placo', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Forro+de+Gesso', thickness: 'Gesso', feature: 'Isolamento térmico e acabamento liso', environments: 'Salas, Quartos, Escritórios', description: 'Acabamento sofisticado que permite embutir iluminação e ar condicionado.' },
    { name: 'Forro PVC Decorativo', type: 'forro', brand: 'Forros Lev, Nova Forros', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Forro+PVC', thickness: 'PVC', feature: 'Resistente à umidade e fácil limpeza', environments: 'Banheiros, Cozinhas, Áreas Molhadas', description: 'Ideal para banheiros, cozinhas e áreas molhadas. Resistente e de fácil manutenção.' },
    { name: 'Rodapés Diversos', type: 'rodape', brand: 'Diversas', img: 'https://placehold.co/800x800/F5F3F0/4A4A4A?text=Rodapés', thickness: 'MDF, PVC, Madeira, Poliestireno', feature: 'Combina com qualquer piso', environments: 'Todos os ambientes', description: 'Linha completa de rodapés em diversos materiais e acabamentos para combinar com qualquer piso.' },
];

const CONTACT_INFO = {
    phone: '(19) 98914-4191',
    address: 'Av. Francisco de Paula Leite, 230 - Jardim Pedroso, Indaiatuba - SP, 13343-040',
    maps_url: 'https://g.co/kgs/Yh4p1XZ',
    maps_embed_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3667.653303689448!2d-47.21415068499244!3d-23.182404984872225!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94c8b3687796d85f%3A0x1b1c2b539a2d2c1!2sAv.%20Francisco%20de%20Paula%20Leite%2C%20230%20-%20Jardim%20Pedroso%2C%20Indaiatuba%20-%20SP%2C%2013343-040!5e0!3m2!1spt-BR!2sbr!4v1688000000000!5m2!1spt-BR!2sbr',
    hours: [
        { day: 'Segunda a Quinta', time: '09:00 - 19:00' },
        { day: 'Sexta-feira', time: '09:00 - 17:00' },
        { day: 'Sábado e Domingo', time: 'Fechado' },
    ],
    whatsapp_phone: '5519989144191',
};
const WHATSAPP_MESSAGE = 'Olá! Vim pelo site da Dsanzo e gostaria de solicitar um orçamento. Aguardo contato!';
const WHATSAPP_URL = `https://wa.me/${CONTACT_INFO.whatsapp_phone}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

// Reusable Components
const CTAButton = ({ children, className = '', as = 'a', icon: Icon, variant = 'primary', onClick, type, disabled }) => {
  const variants = { primary: 'bg-[#D67441] text-white hover:bg-opacity-90 shadow-lg hover:shadow-xl', secondary: 'bg-white text-[#D67441] hover:bg-gray-100 shadow-md', };
  const baseClasses = `group font-bold py-3 px-6 rounded-lg transform hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-2 ${variants[variant]}`;
  if (as === 'button') { return <button type={type || 'button'} onClick={onClick} className={cl(baseClasses, className)} disabled={disabled}>{children} {Icon && <Icon className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />}</button>; }
  return ( <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={onClick} className={cl(baseClasses, className)}>{children} {Icon && <Icon className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />}</a> );
};
const SectionTitle = ({ children, className = '' }) => ( <h2 className={cl('text-3xl md:text-5xl font-bold text-center font-title text-[#2C5F5D]', className)}>{children}</h2>);
const SectionSubtitle = ({ children, className = '' }) => (<p className={cl('text-center text-lg text-[#4A4A4A] mt-4 max-w-3xl mx-auto', className)}>{children}</p>);

// Carousel Component
const Carousel = ({ children, settings = {} }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const timeoutRef = useRef(null);
    const count = React.Children.count(children);
    const resetTimeout = () => { if (timeoutRef.current) { clearTimeout(timeoutRef.current); } };
    useEffect(() => {
        if (settings.autoPlay !== false && count > 1) {
            resetTimeout();
            timeoutRef.current = setTimeout( () => setCurrentIndex((prevIndex) => (prevIndex + 1) % count), settings.delay || 5000 );
        }
        return () => resetTimeout();
    }, [currentIndex, children, settings.delay, settings.autoPlay, count]);
    const changeSlide = (index) => {
        if (index < 0) { setCurrentIndex(count - 1); }
        else if (index >= count) { setCurrentIndex(0); }
        else { setCurrentIndex(index); }
    };
    return (
        <div className="relative w-full">
            <div className="overflow-hidden">
                <div className="flex transition-transform ease-in-out duration-700" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                    {React.Children.map(children, (child) => (<div className="w-full flex-shrink-0">{child}</div>))}
                </div>
            </div>
            {settings.arrows && count > 1 && (
                <div className="absolute top-1/2 -translate-y-1/2 w-full flex justify-between px-2 sm:px-0">
                    <button onClick={() => changeSlide(currentIndex - 1)} className="bg-white/70 p-3 rounded-full hover:bg-white transition-colors z-10 shadow-lg"><ChevronLeft className="text-[#2C5F5D]" /></button>
                    <button onClick={() => changeSlide(currentIndex + 1)} className="bg-white/70 p-3 rounded-full hover:bg-white transition-colors z-10 shadow-lg"><ChevronRight className="text-[#2C5F5D]" /></button>
                </div>
            )}
            {settings.dots && count > 1 && (
                 <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                    {React.Children.map(children, (_, index) => (<button key={index} onClick={() => setCurrentIndex(index)} className={cl('w-2.5 h-2.5 rounded-full transition-all duration-300', currentIndex === index ? 'bg-[#D67441] w-6' : 'bg-gray-300 hover:bg-gray-400')}></button>))}
                </div>
            )}
        </div>
    );
};
const Lightbox = ({ item, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!item) return null;

    const changeSlide = (direction) => {
        const totalImages = item.gallery.length;
        const newIndex = (currentIndex + direction + totalImages) % totalImages;
        setCurrentIndex(newIndex);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-[999] flex items-center justify-center animate-fade-in p-4" onClick={onClose}>
            <div className="relative bg-white w-full max-w-4xl max-h-full overflow-y-auto rounded-xl shadow-2xl grid lg:grid-cols-2" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-2 right-2 md:top-4 md:right-4 text-gray-500 bg-white/50 rounded-full p-2 hover:bg-gray-200 transition-all z-20">
                    <X size={24} />
                </button>
                <div className="relative group">
                    <img src={item.gallery[currentIndex]} alt={`${item.title} - Foto ${currentIndex + 1}`} className="w-full h-64 lg:h-full object-cover rounded-t-xl lg:rounded-l-xl lg:rounded-tr-none"/>
                    {item.gallery.length > 1 && (
                        <>
                            <button onClick={(e) => { e.stopPropagation(); changeSlide(-1);}} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/50 p-2 rounded-full hover:bg-white transition-opacity opacity-0 group-hover:opacity-100">
                                <ChevronLeft className="text-[#2C5F5D]" />
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); changeSlide(1);}} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/50 p-2 rounded-full hover:bg-white transition-opacity opacity-0 group-hover:opacity-100">
                                <ChevronRight className="text-[#2C5F5D]" />
                            </button>
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                                {item.gallery.map((_, index) => (
                                    <div key={index} className={cl("h-2 rounded-full transition-all", currentIndex === index ? "w-4 bg-[#D67441]" : "w-2 bg-white/50")}></div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="p-6 md:p-8 flex flex-col">
                    <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-semibold px-3 py-1 rounded-full text-sm self-start">{item.category}</span>
                    <h2 className="text-2xl md:text-3xl font-bold text-[#2C5F5D] font-title mt-4">{item.title}</h2>
                    <div className="flex items-center gap-2 mt-4 bg-gray-100 p-3 rounded-lg">
                        <Layers size={20} className="text-[#D67441] flex-shrink-0"/>
                        <p className="text-sm text-[#4A4A4A]"><strong>Materiais:</strong> {item.materials}</p>
                    </div>
                    <p className="text-gray-600 mt-6 flex-grow">{item.long_description}</p>
                    <div className="mt-8">
                       <CTAButton icon={ArrowRight}>Quero um projeto assim</CTAButton>
                    </div>
                </div>
            </div>
        </div>
    );
};
const AccordionItem = ({ q, a, isOpen, onClick }) => {
    const contentRef = useRef(null);
    return (
        <div className="border-b border-gray-200">
            <button onClick={onClick} className="w-full flex justify-between items-center text-left py-5 px-2">
                <span className="text-lg font-semibold text-[#2C5F5D]">{q}</span>
                {isOpen ? <Minus className="text-[#D67441]" /> : <Plus className="text-[#4A4A4A]" />}
            </button>
            <div ref={contentRef} className="overflow-hidden transition-all duration-500 ease-in-out" style={{ maxHeight: isOpen ? `${contentRef.current.scrollHeight}px` : '0px' }}>
                <div className="p-2 pb-5 text-[#4A4A4A]">
                    <p>{a}</p>
                </div>
            </div>
        </div>
    );
};

// HOME PAGE - MASTER UPGRADE
const HomePage = ({ onNavigate }) => {
    const [currentTestimonial, setCurrentTestimonial] = useState(0);
    const { categories } = useProducts();
    
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    const stats = [
        { number: '40+', label: 'Anos de Tradição', icon: <Award size={24} /> },
        { number: '200%', label: 'Crescimento em 2024', icon: <TrendingUp size={24} /> },
        { number: '98%', label: 'Clientes Satisfeitos', icon: <Star size={24} /> },
        { number: '100%', label: 'Garantia Total', icon: <ShieldCheck size={24} /> }
    ];

    const comboSteps = [
        {
            number: '01',
            title: 'Consultoria',
            description: 'Visitamos seu espaço e desenvolvemos um projeto personalizado',
            icon: <Users size={32} />,
            color: 'from-[#2C5F5D] to-[#1e4442]'
        },
        {
            number: '02',
            title: 'Materiais',
            description: 'Selecionamos os melhores produtos das marcas mais confiáveis',
            icon: <Layers size={32} />,
            color: 'from-[#D67441] to-[#B85A35]'
        },
        {
            number: '03',
            title: 'Instalação',
            description: 'Nossa equipe própria realiza a instalação com perfeição',
            icon: <Wrench size={32} />,
            color: 'from-[#2C5F5D] to-[#1e4442]'
        },
        {
            number: '04',
            title: 'Garantia',
            description: 'Entregamos com garantia total e suporte contínuo',
            icon: <ShieldCheck size={32} />,
            color: 'from-[#D67441] to-[#B85A35]'
        }
    ];

    return (
        <div className="animate-fade-in">
            {/* Hero Section Revolucionária */}
            <section className="relative bg-gradient-to-br from-[#2C5F5D] via-[#1e4442] to-[#0f2221] text-white overflow-hidden">
    <div className="absolute inset-0 bg-black/20"></div>
    <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#3d7c78]/30 to-transparent"></div>
    <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-[#1a3d3b]/40 to-transparent"></div>
    
    <div className="relative container mx-auto px-6 py-16 min-h-[90vh] flex items-center">
        <div className="grid lg:grid-cols-2 gap-16 items-center w-full">
            <div className="text-center lg:text-left">
                <Animated>
                    <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                        Desde 1980 • Tradição Familiar
                    </span>
                </Animated>
                <Animated>
                    <h1 className="font-title text-5xl md:text-7xl font-extrabold leading-tight mb-6">
                        Transforme Ambientes com o 
                        <span className="text-[#D67441] block">Combo Dsanzo</span>
                    </h1>
                </Animated>
                <Animated>
                    <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
                        <strong className="text-white">Projeto + Material + Instalação + Garantia</strong> tudo em um só lugar. 
                        Há mais de 40 anos, a família Dsanzo cuida de cada detalhe da sua reforma.
                    </p>
                </Animated>

                {/* IMAGEM MOBILE - POSIÇÃO CORRIGIDA: entre descrição e botões */}
                <div className="relative lg:hidden mb-8">
                    <Animated className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#D67441] to-[#2C5F5D] rounded-3xl transform rotate-6"></div>
                        <img 
                            loading="lazy" 
                            src="https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=1227&q=80" 
                            alt="Ambiente moderno transformado pelo Grupo Dsanzo" 
                            className="relative w-full h-80 object-cover rounded-3xl shadow-2xl"
                        />
                        {/* BOX DE GARANTIA CORRIGIDA PARA MOBILE */}
                        <div className="absolute -bottom-4 left-4 right-4 bg-white p-4 rounded-xl shadow-xl">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#D67441] rounded-full flex items-center justify-center flex-shrink-0">
                                    <ShieldCheck size={20} className="text-white" />
                                </div>
                                <div>
                                    <p className="font-bold text-[#2C5F5D] text-sm">Garantia Total</p>
                                    <p className="text-xs text-gray-600">Produtos + Instalação</p>
                                </div>
                            </div>
                        </div>
                    </Animated>
                </div>
                
                <Animated className="flex flex-col sm:flex-row gap-4 mb-12">
                    <CTAButton icon={ArrowRight} className="text-lg px-8 py-4 bg-[#D67441] hover:bg-[#B85A35]">
                        Quero Meu Combo
                    </CTAButton>
                    <a 
                        href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                        className="flex items-center justify-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-6 py-3 rounded-lg hover:border-[#D67441]"
                    >
                        <Phone size={20} />
                        Fale com a Família Dsanzo
                    </a>
                </Animated>

                {/* Prova Social Rápida */}
                <Animated className="flex items-center justify-center lg:justify-start gap-8 text-sm">
                    <div className="flex items-center gap-2">
                        <div className="flex -space-x-2">
                            {testimonials.slice(0, 3).map((testimonial, index) => (
                                <img 
                                    key={index}
                                    src={testimonial.img} 
                                    alt={testimonial.author}
                                    className="w-8 h-8 rounded-full border-2 border-white object-cover"
                                />
                            ))}
                        </div>
                        <span className="text-gray-300">+12000 clientes satisfeitos</span>
                    </div>
                    <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                            <Star key={i} className="text-[#D67441]" fill="#D67441" size={16} />
                        ))}
                        <span className="text-gray-300 ml-1">4.9/5</span>
                    </div>
                </Animated>
            </div>

            {/* IMAGEM DESKTOP - MANTIDA COMO ESTAVA */}
            <div className="relative hidden lg:block">
                <Animated className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#D67441] to-[#2C5F5D] rounded-3xl transform rotate-6"></div>
                    <img 
                        loading="lazy" 
                        src="https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=1227&q=80" 
                        alt="Ambiente moderno transformado pelo Grupo Dsanzo" 
                        className="relative w-full h-96 lg:h-[500px] object-cover rounded-3xl shadow-2xl"
                    />
                    {/* BOX DE GARANTIA DESKTOP - MANTIDA COMO ESTAVA */}
                    <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-[#D67441] rounded-full flex items-center justify-center">
                                <ShieldCheck size={24} className="text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-[#2C5F5D]">Garantia Total</p>
                                <p className="text-sm text-gray-600">Produtos + Instalação</p>
                            </div>
                        </div>
                    </div>
                </Animated>
            </div>
        </div>
    </div>
</section>

            {/* Seção de Estatísticas Impactantes */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Números que Comprovam Nossa Excelência
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                            Quatro décadas construindo confiança, uma reforma de cada vez.
                        </p>
                    </Animated>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <Animated key={index} className="text-center group">
                                <div className="bg-[#F5F3F0] p-8 rounded-2xl hover:bg-[#2C5F5D] transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl">
                                    <div className="text-[#D67441] group-hover:text-white mb-4 flex justify-center transition-colors duration-300">
                                        {stat.icon}
                                    </div>
                                    <div className="text-4xl font-bold text-[#2C5F5D] group-hover:text-white font-title mb-2 transition-colors duration-300">
                                        {stat.number}
                                    </div>
                                    <p className="text-[#4A4A4A] group-hover:text-gray-200 font-medium transition-colors duration-300">
                                        {stat.label}
                                    </p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* O Exclusivo COMBO Dsanzo */}
            <section className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4">
                            Nosso Diferencial Exclusivo
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-6">
                            O COMBO que Revoluciona Reformas
                        </h2>
                        <p className="text-xl text-[#4A4A4A] max-w-4xl mx-auto leading-relaxed">
                            Desde 1980, desenvolvemos um método único que elimina a dor de cabeça das reformas. 
                            <strong className="text-[#2C5F5D]"> Você não precisa mais coordenar fornecedores, instaladores e garantias separadas.</strong>
                        </p>
                    </Animated>

                    <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
                        <Animated>
                            <div className="relative">
                                <img 
                                    loading="lazy" 
                                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1170&q=80" 
                                    alt="Família Dsanzo - Levy e Úbya" 
                                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                                />
                                <div className="absolute -bottom-6 -right-6 bg-[#2C5F5D] text-white p-6 rounded-2xl shadow-xl">
                                    <p className="font-bold text-lg">Levy & Úbya</p>
                                    <p className="text-sm opacity-90">Fundadores • Desde 1980</p>
                                </div>
                            </div>
                        </Animated>
                        
                        <Animated>
                            <h3 className="text-3xl font-bold text-[#2C5F5D] font-title mb-6">
                                A Paixão Familiar que Deu Início a Tudo
                            </h3>
                            <p className="text-lg text-[#4A4A4A] mb-6 leading-relaxed">
                                <strong className="text-[#2C5F5D]">Levy e Úbya Dsanzo</strong> começaram em 1980 com um sonho simples: 
                                transformar a dor de cabeça de uma reforma em uma experiência de tranquilidade e confiança.
                            </p>
                            <p className="text-lg text-[#4A4A4A] mb-8 leading-relaxed">
                                Com Levy trazendo conhecimento técnico desde os 14 anos e Úbya cuidando de cada detalhe, 
                                criaram mais que uma empresa: <strong className="text-[#2C5F5D]">um legado de qualidade e afeto.</strong>
                            </p>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-[#D67441] rounded-full flex items-center justify-center">
                                    <Users size={24} className="text-white" />
                                </div>
                                <div>
                                    <p className="font-bold text-[#2C5F5D]">Empresa 100% Familiar</p>
                                    <p className="text-sm text-[#4A4A4A]">Tradição que passa de geração em geração</p>
                                </div>
                            </div>
                        </Animated>
                    </div>

                    {/* Processo COMBO em 4 Etapas */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {comboSteps.map((step, index) => (
                            <Animated key={index} className="text-center group">
                                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 h-full relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-16 h-16 bg-[#F5F3F0] rounded-bl-2xl flex items-center justify-center">
                                        <span className="text-[#2C5F5D] font-bold text-sm">{step.number}</span>
                                    </div>
                                    
                                    <div className={cl(
                                        'w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300',
                                        step.color
                                    )}>
                                        {step.icon}
                                    </div>
                                    
                                    <h3 className="text-xl font-bold text-[#2C5F5D] font-title mb-4 group-hover:text-[#D67441] transition-colors duration-300">
                                        {step.title}
                                    </h3>
                                    <p className="text-[#4A4A4A] leading-relaxed">{step.description}</p>
                                </div>
                            </Animated>
                        ))}
                    </div>

                    <Animated className="text-center mt-16">
                        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-4xl mx-auto">
                            <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-4">
                                Por Que Nosso COMBO é Único?
                            </h3>
                            <div className="grid md:grid-cols-3 gap-6 text-left">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-[#D67441] rounded-full flex items-center justify-center flex-shrink-0">
                                        <Star size={16} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-[#2C5F5D] mb-1">Um Só Responsável</p>
                                        <p className="text-sm text-[#4A4A4A]">Você trata tudo conosco, sem intermediários</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-[#D67441] rounded-full flex items-center justify-center flex-shrink-0">
                                        <ShieldCheck size={16} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-[#2C5F5D] mb-1">Garantia Total</p>
                                        <p className="text-sm text-[#4A4A4A]">Produtos e instalação com garantia única</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 bg-[#D67441] rounded-full flex items-center justify-center flex-shrink-0">
                                        <Users size={16} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-[#2C5F5D] mb-1">Equipe Própria</p>
                                        <p className="text-sm text-[#4A4A4A]">Instaladores treinados pela família Dsanzo</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Animated>
                </div>
            </section>

            {/* Duas Marcas, Uma Essência */}
            <section className="py-20 bg-[#2C5F5D] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold font-title mb-4">
                            Duas Marcas, Uma Essência
                        </h2>
                        <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                            Para atender cada cliente de forma única, o Grupo Dsanzo se divide em duas frentes, 
                            cada uma com sua especialidade, mas ambas com a mesma promessa de qualidade.
                        </p>
                    </Animated>
                    
                    <div className="grid lg:grid-cols-2 gap-12">
                        <Animated className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-[#D67441] rounded-2xl flex items-center justify-center">
                                    <Home size={32} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold font-title text-[#D67441]">DK DKOR</h3>
                                    <p className="text-gray-200">Para o Cliente Final</p>
                                </div>
                            </div>
                            <p className="text-gray-200 mb-6 leading-relaxed">
                                A solução completa para quem busca uma reforma sem estresse. Oferecemos o COMBO exclusivo: 
                                projeto personalizado, materiais de qualidade e instalação com nossa equipe própria.
                            </p>
                            <ul className="space-y-3">
                                {['Atendimento residencial e comercial', 'COMBO completo com garantia', 'Projeto personalizado incluso', 'Equipe própria de instalação'].map((item, index) => (
                                    <li key={index} className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-[#D67441] rounded-full"></div>
                                        <span className="text-gray-200">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </Animated>

                        <Animated className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-16 h-16 bg-[#D67441] rounded-2xl flex items-center justify-center">
                                    <Briefcase size={32} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold font-title text-[#D67441]">Levs Revest</h3>
                                    <p className="text-gray-200">Para Profissionais</p>
                                </div>
                            </div>
                            <p className="text-gray-200 mb-6 leading-relaxed">
                                A parceira ideal para instaladores, arquitetos, construtoras e lojistas. Oferecemos os melhores 
                                produtos no atacado, com suporte técnico e treinamento especializado.
                            </p>
                            <ul className="space-y-3">
                                {['Preços especiais no atacado', 'Treinamento gratuito com Levy', 'Suporte técnico especializado', 'Parceria sem concorrência'].map((item, index) => (
                                    <li key={index} className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-[#D67441] rounded-full"></div>
                                        <span className="text-gray-200">{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* Produtos em Destaque Reformulado */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Soluções para Todos os Ambientes
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Das melhores marcas do mercado, produtos que unem estética, durabilidade e o melhor custo-benefício.
                        </p>
                    </Animated>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
                        {(categories.filter(c => c.active !== false).length > 0 ? categories.filter(c => c.active !== false) : products).map((item, index) => (
                            <Animated key={item.title || item.name} className="group relative rounded-xl overflow-hidden shadow-lg cursor-pointer h-80 lg:h-96" onClick={() => onNavigate('Produtos')}>
                                <img loading="lazy" src={item.image || item.img} alt={item.title || item.name} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent transition-all duration-500 group-hover:from-black/90 group-hover:via-black/60"></div>
                                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                    <h3 className="text-2xl font-bold font-title transform transition-transform duration-500 translate-y-20 group-hover:-translate-y-2">{item.title || item.name}</h3>
                                    <div className="opacity-0 transform transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 translate-y-4 pt-2">
                                        <p className="text-sm text-gray-300 mb-4">{item.subtitle || 'Ver todas as opções'}</p>
                                        <div className="w-10 h-10 bg-[#D67441] rounded-full flex items-center justify-center">
                                            <ArrowRight size={20} />
                                        </div>
                                    </div>
                                </div>
                            </Animated>
                        ))}
                    </div>

                    <Animated className="flex justify-center">
                        <CTAButton as="button" onClick={() => onNavigate('Produtos')} variant="secondary" icon={ArrowRight}>
                            Ver Catálogo Completo
                        </CTAButton>
                    </Animated>
                </div>
            </section>

            {/* === PARCEIROS DE CONFIANÇA - UPGRADE COMPLETO === */}
<section className="py-20 bg-white">
    <div className="container mx-auto px-6">
         <Animated>
            <SectionTitle>Parceiros de Confiança</SectionTitle>
            <SectionSubtitle>Trabalhamos exclusivamente com as marcas mais respeitadas do mercado, garantindo qualidade e durabilidade em cada produto.</SectionSubtitle>
        </Animated>
    </div>
    <Animated className="mt-16 w-full inline-flex flex-nowrap overflow-hidden [mask-image:_linear-gradient(to_right,transparent_0,_black_128px,_black_calc(100%-128px),transparent_100%)]">
        <ul className="flex items-center justify-center md:justify-start [&_li]:mx-6 animate-infinite-scroll">
            {partnersData.map((partner, index) => (
                <li key={index}>
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 w-48 h-28 flex flex-col justify-center items-center text-center">
                        <h4 className="font-bold text-lg text-[#2C5F5D] font-title leading-tight mb-1">
                            {partner.main}
                        </h4>
                        <p className="text-sm text-[#4A4A4A] font-medium leading-tight">
                            {partner.sub}
                        </p>
                    </div>
                </li>
            ))}
        </ul>
        <ul className="flex items-center justify-center md:justify-start [&_li]:mx-6 animate-infinite-scroll" aria-hidden="true">
            {partnersData.map((partner, index) => (
                <li key={`clone-${index}`}>
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 w-48 h-28 flex flex-col justify-center items-center text-center">
                        <h4 className="font-bold text-lg text-[#2C5F5D] font-title leading-tight mb-1">
                            {partner.main}
                        </h4>
                        <p className="text-sm text-[#4A4A4A] font-medium leading-tight">
                            {partner.sub}
                        </p>
                    </div>
                </li>
            ))}
        </ul>
    </Animated>
</section>

            {/* Depoimentos Dinâmicos */}
            <section className="py-20 bg-[#2C5F5D] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold font-title mb-4">
                            A Confiança de Quem Já Transformou
                        </h2>
                        <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                            Mais que clientes, conquistamos amigos. Veja o que dizem sobre nossa família e nosso trabalho.
                        </p>
                    </Animated>
                    
                    <div className="max-w-4xl mx-auto">
                        <Animated className="bg-white/10 p-8 md:p-12 rounded-2xl backdrop-blur-sm border border-white/20 text-center">
                            <div className="flex justify-center mb-6">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="text-[#D67441]" fill="#D67441" size={24} />
                                ))}
                            </div>
                            <p className="text-xl md:text-2xl text-gray-200 italic mb-8 leading-relaxed">
                                "{testimonials[currentTestimonial].text}"
                            </p>
                            <div className="flex items-center justify-center gap-4">
                                <img 
                                    loading="lazy" 
                                    src={testimonials[currentTestimonial].img} 
                                    alt={testimonials[currentTestimonial].author} 
                                    className="w-16 h-16 rounded-full object-cover border-2 border-[#D67441]"
                                />
                                <div className="text-left">
                                    <p className="font-bold text-xl text-white">{testimonials[currentTestimonial].author}</p>
                                    <p className="text-[#D67441] font-semibold">{testimonials[currentTestimonial].role}</p>
                                </div>
                            </div>
                        </Animated>
                        
                        <div className="flex justify-center mt-8 space-x-2">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentTestimonial(index)}
                                    className={cl(
                                        'w-3 h-3 rounded-full transition-all duration-300',
                                        currentTestimonial === index ? 'bg-[#D67441] w-8' : 'bg-white/30 hover:bg-white/50'
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Final Poderoso */}
            <section className="py-20 bg-gradient-to-br from-[#2C5F5D] via-[#1e4442] to-[#0f2221] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-[#3d7c78]/20 rounded-full"></div>
                    <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#1a3d3b]/30 rounded-full"></div>
                </div>
                
                <div className="relative container mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <Animated>
                            <h2 className="text-3xl md:text-5xl font-bold font-title mb-6">
                                Pronto para Transformar Seu Ambiente 
                                <span className="text-[#D67441]"> Sem Dor de Cabeça?</span>
                            </h2>
                            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                                Há mais de 40 anos, a família Dsanzo cuida de cada detalhe da sua reforma. 
                                <strong className="text-white"> Solicite seu COMBO gratuito e descubra a diferença!</strong>
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4 mb-8">
                                <CTAButton icon={ArrowRight} className="text-lg px-8 py-4 bg-[#D67441] hover:bg-[#B85A35]">
                                    Quero Meu COMBO Grátis
                                </CTAButton>
                                <a 
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-6 py-3 rounded-lg hover:border-[#D67441]"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/>
                                    </svg>
                                    WhatsApp Direto
                                </a>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-6 text-center">
                                {[
                                    { icon: <Users size={24} />, text: 'Consultoria Gratuita' },
                                    { icon: <ShieldCheck size={24} />, text: 'Garantia Total' },
                                    { icon: <Star size={24} />, text: 'Tradição Familiar' }
                                ].map((item, index) => (
                                    <div key={index} className="flex flex-col items-center gap-2">
                                        <div className="text-[#D67441]">{item.icon}</div>
                                        <span className="text-sm font-semibold">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </Animated>
                        
                        <Animated className="relative">
                            <div className="bg-white p-8 rounded-2xl shadow-2xl">
                                <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-6 text-center">
                                    Solicite Seu COMBO Gratuito
                                </h3>
                                <form name="home-combo" method="POST" data-netlify="true" action="/" className="space-y-4">
                                    <input type="hidden" name="form-name" value="home-combo" />
                                    <p className="hidden"><label>Não preencha: <input name="bot-field" /></label></p>
                                    <div>
                                        <input 
                                            type="text" 
                                            name="name"
                                            placeholder="Seu nome completo" 
                                            className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] text-gray-800"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <input 
                                            type="tel" 
                                            name="phone"
                                            placeholder="Seu WhatsApp" 
                                            className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] text-gray-800"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <textarea 
                                            name="message"
                                            placeholder="Conte sobre seu projeto..." 
                                            rows="3"
                                            className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] text-gray-800"
                                            required
                                        ></textarea>
                                    </div>
                                    <CTAButton as="button" type="submit" className="w-full text-lg py-4" icon={ArrowRight}>
                                        Solicitar COMBO Grátis
                                    </CTAButton>
                                </form>
                                <p className="text-xs text-gray-500 text-center mt-4">
                                    Resposta em até 2 horas • Atendimento da família Dsanzo
                                </p>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>
        </div>
    );
};


// ABOUT PAGE - COMPLETELY FIXED AND REDESIGNED
const AboutPage = () => {
    const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
    
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentStoryIndex((prev) => (prev + 1) % familyStories.length);
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    const familyStories = [
        {
            year: "1980",
            title: "O Sonho Começou",
            story: "Levy, com apenas 14 anos, já trabalhava com pisos. Úbya trouxe o cuidado e a visão de futuro. Juntos, eles plantaram a semente do que seria o Grupo Dsanzo.",
            emotion: "Determinação"
        },
        {
            year: "1993",
            title: "A Primeira Loja",
            story: "De prestadores de serviço a lojistas. A família Dsanzo criou algo único: não apenas vender, mas cuidar de todo o processo. Nascia o conceito COMBO.",
            emotion: "Inovação"
        },
        {
            year: "2010",
            title: "O Desafio",
            story: "Nem tudo foram flores. O mercado em Monte Mor não deu certo. Mas a família Dsanzo aprendeu que cada queda ensina, cada erro fortalece.",
            emotion: "Resiliência"
        },
        {
            year: "2024",
            title: "O Renascimento",
            story: "Crescimento de 50% em um ano. Duas marcas consolidadas. A família que começou com um sonho hoje transforma milhares de ambientes pelo Brasil.",
            emotion: "Conquista"
        }
    ];

    const timelineData = [
        { 
            year: 1980, 
            title: 'Fundação', 
            text: 'Levy e Úbya fundam o Grupo Dsanzo em Campinas, SP. Levy, com conhecimento técnico desde os 14 anos, e Úbya, com visão de futuro, criam mais que uma empresa: um legado familiar.', 
            icon: <Sparkles/>,
            milestone: "O Início de Tudo"
        },
        { 
            year: 1993, 
            title: 'Primeira Loja', 
            text: 'De prestadores de serviço a lojistas. Nasce o conceito revolucionário COMBO: Projeto + Material + Instalação + Garantia. Uma solução que ninguém mais oferecia.', 
            icon: <Home/>,
            milestone: "A Inovação"
        },
        { 
            year: 1997, 
            title: 'Expansão Atacado', 
            text: 'Tornamo-nos distribuidores de marcas renomadas como Durafloor, Eucafloor e Tabacow. O atacado nasce com um diferencial: nunca concorrer com nossos parceiros.', 
            icon: <Briefcase/>,
            milestone: "Crescimento Estratégico"
        },
        { 
            year: 2010, 
            title: 'O Desafio', 
            text: 'Tentativa de diversificação com mercado em Monte Mor não deu certo. Dificuldades financeiras levaram à interrupção temporária. Mas a família Dsanzo não desiste.', 
            icon: <TrendingUp/>,
            milestone: "Aprendizado"
        },
        { 
            year: 2012, 
            title: 'Recomeço', 
            text: 'Retomada das operações em Campinas, focando exclusivamente no varejo com o modelo COMBO. A experiência trouxe maturidade e foco.', 
            icon: <Star/>,
            milestone: "Renascimento"
        },
        { 
            year: 2015, 
            title: 'DK DKOR', 
            text: 'Estabelecimento em Indaiatuba com a marca DK DKOR Soluções em Interiores. Consolidação definitiva no varejo com atendimento humanizado.', 
            icon: <Award/>,
            milestone: "Consolidação"
        },
        { 
            year: 2024, 
            title: 'Grupo Dsanzo', 
            text: 'Retomada do atacado com Levs Revest. Crescimento de 50% no ano. Duas marcas, uma essência: cuidar de pessoas através de ambientes transformados.', 
            icon: <Building/>,
            milestone: "O Futuro é Agora"
        }
    ];

    const values = [
        {
            icon: <Users size={32} className="text-[#D67441]" />,
            title: "Família",
            description: "Mais que uma empresa, somos uma família que trata cada cliente como parte dela. O carinho e cuidado pessoal fazem a diferença.",
            story: "Desde 1980, cada decisão é tomada pensando no bem-estar das pessoas. Não é apenas negócio, é afeto."
        },
        {
            icon: <ShieldCheck size={32} className="text-[#D67441]" />,
            title: "Confiança",
            description: "Nossa palavra é nosso maior patrimônio. Quando prometemos, cumprimos. Quando garantimos, honramos.",
            story: "Em 44 anos, construímos nossa reputação cliente por cliente, projeto por projeto, sempre com transparência total."
        },
        {
            icon: <Lightbulb size={32} className="text-[#D67441]" />,
            title: "Inovação",
            description: "Criamos o modelo COMBO quando ninguém pensava nisso. Continuamos inovando para facilitar a vida dos nossos clientes.",
            story: "Não seguimos tendências, criamos soluções. O COMBO nasceu da necessidade real de simplificar reformas."
        },
        {
            icon: <Star size={32} className="text-[#D67441]" />,
            title: "Excelência",
            description: "Cada projeto é único. Cada instalação é perfeita. Cada cliente sai satisfeito. Não aceitamos menos que isso.",
            story: "Levy ensina pessoalmente cada instalador. O padrão Dsanzo não é negociável: tem que ser perfeito."
        }
    ];

    const achievements = [
        { number: "44", label: "Anos de História", suffix: "" },
        { number: "50", label: "Crescimento em 2024", suffix: "%" },
        { number: "98", label: "Clientes Satisfeitos", suffix: "%" },
        { number: "2", label: "Marcas Consolidadas", suffix: "" }
    ];

    return (
        <div className="animate-fade-in bg-white overflow-hidden">
            {/* Hero Section Simples e Limpa */}
            <section className="bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] text-white py-24 md:py-32">
                <div className="container mx-auto px-6 text-center">
                    <Animated>
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                            Nossa História • Desde 1980
                        </span>
                    </Animated>
                    
                    <Animated>
                        <h1 className="font-title text-4xl md:text-6xl font-extrabold leading-tight mb-6">
                            Mais que uma empresa,
                            <span className="block text-[#D67441]">uma família</span>
                        </h1>
                    </Animated>
                    
                    <Animated>
                        <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-12 leading-relaxed">
                            44 anos transformando ambientes e vidas. A história de <strong className="text-white">Levy e Úbya</strong> que começou 
                            com um sonho e hoje é referência nacional em revestimentos.
                        </p>
                    </Animated>

                    {/* Story Cards com Carrossel Funcional */}
                    <div className="mt-16 max-w-4xl mx-auto">
                        <Animated className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                            <div className="text-center">
                                <span className="text-[#D67441] font-bold text-lg">{familyStories[currentStoryIndex].year}</span>
                                <h3 className="text-2xl md:text-3xl font-bold font-title mb-4">{familyStories[currentStoryIndex].title}</h3>
                                <p className="text-lg text-gray-200 leading-relaxed mb-6">{familyStories[currentStoryIndex].story}</p>
                                <span className="inline-block bg-[#D67441]/20 text-[#D67441] px-4 py-2 rounded-full text-sm font-semibold">
                                    {familyStories[currentStoryIndex].emotion}
                                </span>
                            </div>
                        </Animated>
                        
                        {/* Indicadores Funcionais */}
                        <div className="flex justify-center mt-8 space-x-3">
                            {familyStories.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentStoryIndex(index)}
                                    className={cl(
                                        'w-3 h-3 rounded-full transition-all duration-300',
                                        currentStoryIndex === index 
                                            ? 'bg-[#D67441] w-8' 
                                            : 'bg-white/30 hover:bg-white/50'
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Seção dos Fundadores */}
            <section className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <Animated>
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] rounded-3xl transform rotate-3"></div>
                                <img 
                                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1170&q=80"
                                    alt="Levy e Úbya Dsanzo - Fundadores"
                                    className="relative w-full h-96 lg:h-[500px] object-cover rounded-3xl shadow-2xl"
                                />
                                <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl border-l-4 border-[#D67441]">
                                    <p className="font-bold text-2xl text-[#2C5F5D]">1980</p>
                                    <p className="text-sm text-gray-600">Início da jornada</p>
                                </div>
                            </div>
                        </Animated>
                        
                        <Animated>
                            <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                                Os Fundadores
                            </span>
                            <h2 className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-8 leading-tight">
                                Levy & Úbya: 
                                <span className="block text-[#D67441]">A Paixão que Deu Início a Tudo</span>
                            </h2>
                            
                            <div className="space-y-6 text-lg text-[#4A4A4A] leading-relaxed">
                                <p>
                                    <strong className="text-[#2C5F5D]">Levy</strong> começou no ramo aos 14 anos. Não por necessidade, 
                                    mas por paixão genuína em transformar ambientes. Cada piso que instalava era uma obra de arte.
                                </p>
                                <p>
                                    <strong className="text-[#2C5F5D]">Úbya</strong> trouxe a visão de futuro e o cuidado humano. 
                                    Ela enxergou que não bastava vender produtos - era preciso cuidar de pessoas.
                                </p>
                                <p className="text-xl font-semibold text-[#2C5F5D] bg-white p-6 rounded-xl border-l-4 border-[#D67441]">
                                    "Juntos, criaram mais que uma empresa: um legado de qualidade, confiança e afeto que 
                                    transforma não apenas ambientes, mas vidas."
                                </p>
                            </div>

                            <div className="mt-8 grid grid-cols-2 gap-6">
                                <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                                    <div className="text-3xl font-bold text-[#D67441] font-title">44</div>
                                    <div className="text-sm text-[#4A4A4A] font-semibold">Anos de Dedicação</div>
                                </div>
                                <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                                    <div className="text-3xl font-bold text-[#D67441] font-title">100%</div>
                                    <div className="text-sm text-[#4A4A4A] font-semibold">Empresa Familiar</div>
                                </div>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* Timeline Melhorada */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                            Nossa Jornada
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-8">
                            44 Anos de História
                        </h2>
                        <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
                            Cada marco, cada desafio, cada conquista moldou quem somos hoje. 
                            Uma jornada de persistência, inovação e muito amor pelo que fazemos.
                        </p>
                    </Animated>

                    {/* Timeline Desktop */}
                    <div className="hidden lg:block relative max-w-6xl mx-auto">
                        <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-[#2C5F5D] to-[#1e4442]"></div>
                        
                        {timelineData.map((item, index) => (
                            <Animated key={index} className="relative mb-16">
                                <div className={cl(
                                    "flex items-center",
                                    index % 2 === 0 ? "flex-row" : "flex-row-reverse"
                                )}>
                                    {/* Conteúdo */}
                                    <div className="w-5/12">
                                        <div className={cl(
                                            "bg-white p-8 rounded-2xl shadow-xl border-2 border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2",
                                            index % 2 === 0 ? "mr-8" : "ml-8"
                                        )}>
                                            <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-3 py-1 rounded-full text-xs uppercase tracking-wider mb-4">
                                                {item.milestone}
                                            </span>
                                            <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-4">{item.title}</h3>
                                            <p className="text-[#4A4A4A] leading-relaxed">{item.text}</p>
                                        </div>
                                    </div>

                                    {/* Ícone Central */}
                                    <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] rounded-full border-8 border-white flex items-center justify-center text-white shadow-xl z-10">
                                        {React.cloneElement(item.icon, { size: 24 })}
                                    </div>

                                    {/* Ano */}
                                    <div className="w-5/12 flex justify-center">
                                        <div className="text-5xl font-bold text-[#D67441]/20 font-title">
                                            {item.year}
                                        </div>
                                    </div>
                                </div>
                            </Animated>
                        ))}
                    </div>

                    {/* Timeline Mobile Corrigida */}
                    <div className="lg:hidden space-y-8">
                        {timelineData.map((item, index) => (
                            <Animated key={index} className="flex gap-4">
                                <div className="flex flex-col items-center">
                                    <div className="w-12 h-12 bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] rounded-full flex items-center justify-center text-white shadow-lg flex-shrink-0">
                                        {React.cloneElement(item.icon, { size: 20 })}
                                    </div>
                                    {index < timelineData.length - 1 && (
                                        <div className="w-1 h-16 bg-gradient-to-b from-[#2C5F5D] to-[#1e4442] mt-4"></div>
                                    )}
                                </div>
                                <div className="flex-1 pb-8">
                                    <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-3 py-1 rounded-full text-xs uppercase tracking-wider mb-3">
                                        {item.year} • {item.milestone}
                                    </span>
                                    <h3 className="text-xl font-bold text-[#2C5F5D] font-title mb-3">{item.title}</h3>
                                    <p className="text-[#4A4A4A] leading-relaxed">{item.text}</p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* O Que Nos Move - Layout Corrigido */}
            <section className="py-20 bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                            Nossos Valores
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold font-title mb-8">
                            O Que Nos Move
                        </h2>
                        <p className="text-xl text-gray-200 max-w-4xl mx-auto">
                            Não são apenas palavras bonitas. São princípios que vivemos todos os dias, 
                            em cada projeto, em cada relacionamento, em cada decisão que tomamos.
                        </p>
                    </Animated>

                    {/* Grid de Valores - Mobile Corrigido */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {values.map((value, index) => (
                            <Animated key={index} className="group">
                                <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2 h-full">
                                    <div className="mb-6">
                                        {value.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold font-title mb-4 text-[#D67441]">
                                        {value.title}
                                    </h3>
                                    <p className="text-gray-200 mb-6 leading-relaxed">
                                        {value.description}
                                    </p>
                                    <div className="bg-black/20 p-4 rounded-lg border-l-4 border-[#D67441]">
                                        <p className="text-sm italic text-gray-300">
                                            "{value.story}"
                                        </p>
                                    </div>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* Números que Falam por Si - Corrigido */}
            <section className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                            Nossas Conquistas
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-8">
                            Números que Falam por Si
                        </h2>
                        <p className="text-xl text-[#4A4A4A] max-w-3xl mx-auto">
                            Cada número representa vidas transformadas, sonhos realizados e a confiança 
                            que milhares de famílias depositaram em nós ao longo de quatro décadas.
                        </p>
                    </Animated>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {achievements.map((achievement, index) => (
                            <Animated key={index} className="text-center group">
                                <div className="bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-4 border-t-4 border-[#D67441]">
                                    <div className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-4 group-hover:text-[#D67441] transition-colors duration-300 break-words">
                                        {achievement.number}
                                        {achievement.suffix && (
                                            <span className="text-2xl md:text-3xl text-[#D67441]">{achievement.suffix}</span>
                                        )}
                                    </div>
                                    <p className="text-[#4A4A4A] font-semibold text-sm md:text-base">{achievement.label}</p>
                                </div>
                            </Animated>
                        ))}
                    </div>

                    {/* Missão, Visão e Valores */}
                    <div className="grid lg:grid-cols-3 gap-8 mt-20">
                        <Animated className="bg-white p-8 rounded-2xl shadow-xl border-l-4 border-[#2C5F5D] hover:shadow-2xl transition-all duration-300">
                            <div className="w-16 h-16 bg-[#2C5F5D] rounded-2xl flex items-center justify-center mb-6">
                                <Users size={32} className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold font-title text-[#2C5F5D] mb-4">Nossa Missão</h3>
                            <p className="text-[#4A4A4A] leading-relaxed">
                                Oferecer soluções completas e acessíveis em revestimentos, unindo qualidade de produtos, 
                                excelência na instalação e atendimento personalizado, sempre com compromisso, confiança e responsabilidade.
                            </p>
                        </Animated>

                        <Animated className="bg-white p-8 rounded-2xl shadow-xl border-l-4 border-[#D67441] hover:shadow-2xl transition-all duration-300">
                            <div className="w-16 h-16 bg-[#D67441] rounded-2xl flex items-center justify-center mb-6">
                                <Star size={32} className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold font-title text-[#2C5F5D] mb-4">Nossa Visão</h3>
                            <p className="text-[#4A4A4A] leading-relaxed">
                                Tornar-se uma marca nacional de referência em revestimentos, reconhecida pela inovação 
                                e atendimento completo humanizado, adquirindo sempre muito mais que clientes: amigos.
                            </p>
                        </Animated>

                        <Animated className="bg-white p-8 rounded-2xl shadow-xl border-l-4 border-[#2C5F5D] hover:shadow-2xl transition-all duration-300">
                            <div className="w-16 h-16 bg-[#2C5F5D] rounded-2xl flex items-center justify-center mb-6">
                                <ShieldCheck size={32} className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold font-title text-[#2C5F5D] mb-4">Nossos Valores</h3>
                            <p className="text-[#4A4A4A] leading-relaxed">
                                Qualidade, Confiança, Transparência, Inovação, Família e Sustentabilidade. 
                                Princípios que guiam cada decisão e cada relacionamento que construímos.
                            </p>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* Duas Marcas, Uma Essência */}
            <section className="py-20 bg-[#2C5F5D] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                            Nossas Marcas
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold font-title mb-8">
                            Duas Marcas, Uma Essência
                        </h2>
                        <p className="text-xl text-gray-200 max-w-4xl mx-auto">
                            Para atender cada cliente de forma única, o Grupo Dsanzo se divide em duas frentes, 
                            cada uma com sua especialidade, mas ambas com a mesma promessa de qualidade e confiança.
                        </p>
                    </Animated>
                    
                    <div className="grid lg:grid-cols-2 gap-12">
                        <Animated className="bg-white/10 backdrop-blur-sm p-10 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2">
                            <div className="flex items-center gap-6 mb-8">
                                <div className="w-20 h-20 bg-[#D67441] rounded-2xl flex items-center justify-center">
                                    <Home size={40} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-bold font-title text-[#D67441]">DK DKOR</h3>
                                    <p className="text-gray-200 text-lg">Para o Cliente Final</p>
                                </div>
                            </div>
                            <p className="text-gray-200 mb-8 leading-relaxed text-lg">
                                A solução completa para quem busca uma reforma sem estresse. Oferecemos o COMBO exclusivo: 
                                projeto personalizado, materiais de qualidade e instalação com nossa equipe própria.
                            </p>
                            <div className="space-y-4">
                                {[
                                    'Atendimento residencial e comercial',
                                    'COMBO completo com garantia total',
                                    'Projeto personalizado incluso',
                                    'Equipe própria de instalação',
                                    'Acompanhamento do início ao fim'
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-[#D67441] rounded-full"></div>
                                        <span className="text-gray-200">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </Animated>

                        <Animated className="bg-white/10 backdrop-blur-sm p-10 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2">
                            <div className="flex items-center gap-6 mb-8">
                                <div className="w-20 h-20 bg-[#D67441] rounded-2xl flex items-center justify-center">
                                    <Briefcase size={40} className="text-white" />
                                </div>
                                <div>
                                    <h3 className="text-3xl font-bold font-title text-[#D67441]">Levs Revest</h3>
                                    <p className="text-gray-200 text-lg">Para Profissionais</p>
                                </div>
                            </div>
                            <p className="text-gray-200 mb-8 leading-relaxed text-lg">
                                A parceira ideal para instaladores, arquitetos, construtoras e lojistas. Oferecemos os melhores 
                                produtos no atacado, com suporte técnico e treinamento especializado.
                            </p>
                            <div className="space-y-4">
                                {[
                                    'Preços especiais no atacado',
                                    'Treinamento gratuito com Levy',
                                    'Suporte técnico especializado',
                                    'Parceria sem concorrência',
                                    'Distribuição para todo Sudeste'
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <div className="w-2 h-2 bg-[#D67441] rounded-full"></div>
                                        <span className="text-gray-200">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* CTA Final */}
            <section className="py-20 bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] text-white">
                <div className="container mx-auto px-6 text-center">
                    <Animated>
                        <h2 className="text-3xl md:text-5xl font-bold font-title mb-8">
                            Quer Fazer Parte da 
                            <span className="block text-[#D67441]">Nossa História?</span>
                        </h2>
                    </Animated>
                    <Animated>
                        <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-12 leading-relaxed">
                            44 anos cuidando de famílias como a sua. Centenas de ambientes transformados. 
                            Milhares de sorrisos conquistados. <strong className="text-white">E a sua história? Quando vai começar?</strong>
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <CTAButton icon={ArrowRight} className="text-xl px-10 py-5 bg-[#D67441] hover:bg-[#B85A35]">
                            Quero Conhecer a Família Dsanzo
                        </CTAButton>
                        <a 
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-8 py-4 rounded-lg hover:border-[#D67441] text-lg"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/>
                            </svg>
                            Conversar com Levy e Úbya
                        </a>
                    </Animated>
                </div>
            </section>
        </div>
    );
};

// PRODUCTS PAGE - MASTER UPGRADE WITH SCROLL AND IMPROVEMENTS
const ProductsPage = () => {
    const [filter, setFilter] = useState('*');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showComparison, setShowComparison] = useState(false);
    
    // Ref para a seção de produtos
    const productsGridRef = useRef(null);
    
      const { products: cmsProducts, categories: cmsCategories } = useProducts();
  const activeCmsProducts = (cmsProducts || []).filter(p => p.active !== false);
  const activeCmsCategories = (cmsCategories || []).filter(c => c.active !== false);
  const listProducts = activeCmsProducts.length ? activeCmsProducts : productList;

    const filters = [ 
        { id: '*', label: 'Todos os Produtos', icon: <ShoppingBag size={16} />, count: listProducts.length }, 
        ...((activeCmsCategories && activeCmsCategories.length) 
            ? activeCmsCategories.map(c => ({
                id: c.slug || c.id,
                label: c.title || c.name,
                icon: <Layers size={16} />,
                count: listProducts.filter(p => p.type === (c.slug || c.id)).length
            }))
            : [
                { id: 'vinilico', label: 'Pisos Vinílicos', icon: <Layers size={16} />, count: listProducts.filter(p => p.type === 'vinilico').length }, 
                { id: 'laminado', label: 'Pisos Laminados', icon: <Home size={16} />, count: listProducts.filter(p => p.type === 'laminado').length }, 
                { id: 'carpete', label: 'Carpetes', icon: <Gem size={16} />, count: listProducts.filter(p => p.type === 'carpete').length }, 
                { id: 'forro', label: 'Forros', icon: <Building size={16} />, count: listProducts.filter(p => p.type === 'forro').length }, 
                { id: 'rodape', label: 'Rodapés', icon: <Ruler size={16} />, count: listProducts.filter(p => p.type === 'rodape').length } 
            ]
        )
    ];
    
    const filteredProducts = filter === '*' ? listProducts : listProducts.filter(p => p.type === filter);

    // Função para mudar filtro e fazer scroll
    const handleFilterChange = (filterId) => {
        setFilter(filterId);
        
        // Scroll suave para a seção de produtos após um pequeno delay
        setTimeout(() => {
            if (productsGridRef.current) {
                const headerHeight = 100; // Altura do header fixo
                const elementPosition = productsGridRef.current.offsetTop - headerHeight;
                
                window.scrollTo({
                    top: elementPosition,
                    behavior: 'smooth'
                });
            }
        }, 100);
    };

    const productCategories = [
        {
            id: 'vinilico',
            title: 'Pisos Vinílicos',
            subtitle: 'Modernidade e Praticidade',
            description: 'A escolha inteligente para quem busca beleza, durabilidade e facilidade de manutenção. Resistente à água e com instalação rápida.',
            benefits: ['Resistente à água', 'Instalação rápida', 'Fácil manutenção', 'Conforto térmico'],
            idealFor: 'Residencial, comercial, hospitais e áreas molhadas',
            image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1170&q=80'
        },
        {
            id: 'laminado',
            title: 'Pisos Laminados',
            subtitle: 'Elegância e Resistência',
            description: 'A beleza da madeira com a praticidade moderna. Perfeito para ambientes que exigem resistência e sofisticação.',
            benefits: ['Visual amadeirado', 'Alta resistência', 'Instalação click', 'Ótimo custo-benefício'],
            idealFor: 'Residencial e comercial leve',
            image: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?auto=format&fit=crop&w=1170&q=80'
        },
        {
            id: 'carpete',
            title: 'Carpetes',
            subtitle: 'Conforto e Aconchego',
            description: 'Máximo conforto térmico e acústico. Ideal para criar ambientes acolhedores e sofisticados.',
            benefits: ['Conforto térmico', 'Isolamento acústico', 'Variedade de texturas', 'Antialérgico'],
            idealFor: 'Quartos, salas, escritórios e hotéis',
            image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1170&q=80'
        },
        {
            id: 'forro',
            title: 'Forros',
            subtitle: 'Acabamento Perfeito',
            description: 'Complete seu ambiente com forros que unem funcionalidade e estética. Isolamento térmico e acústico garantidos.',
            benefits: ['Isolamento térmico', 'Acabamento liso', 'Resistente à umidade', 'Fácil instalação'],
            idealFor: 'Todos os ambientes internos',
            image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1170&q=80'
        },
        {
            id: 'rodape',
            title: 'Rodapés',
            subtitle: 'Detalhes que Fazem a Diferença',
            description: 'O acabamento final que valoriza seu projeto. Diversos materiais e acabamentos para combinar com qualquer piso.',
            benefits: ['Diversos materiais', 'Acabamento perfeito', 'Proteção das paredes', 'Fácil instalação'],
            idealFor: 'Complemento para todos os tipos de piso',
            image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1170&q=80'
        }
    ];

    const brandLogos = [
        { name: 'Eucatex', logo: 'https://placehold.co/200x100/ffffff/cccccc?text=Eucatex' },
        { name: 'PIX', logo: 'https://placehold.co/200x100/ffffff/cccccc?text=PIX' },
        { name: 'Tarkett', logo: 'https://placehold.co/200x100/ffffff/cccccc?text=Tarkett' },
        { name: 'Durafloor', logo: 'https://placehold.co/200x100/ffffff/cccccc?text=Durafloor' },
        { name: 'Vinilfloor', logo: 'https://placehold.co/200x100/ffffff/cccccc?text=Vinilfloor' },
        { name: 'Arquitech', logo: 'https://placehold.co/200x100/ffffff/cccccc?text=Arquitech' }
    ];

    return (
        <div className="animate-fade-in bg-white">
            {/* Hero Section Revolucionária */}
            <section className="relative bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] text-white py-32 overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#3d7c78]/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-[#1a3d3b]/40 to-transparent"></div>
                
                <div className="relative container mx-auto px-6 text-center">
                    <Animated>
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                            Catálogo Completo • Desde 1980
                        </span>
                    </Animated>
                    <Animated>
                        <h1 className="font-title text-4xl md:text-6xl font-extrabold leading-tight mb-6">
                            Soluções Completas para 
                            <span className="block text-[#D67441]">Todos os Ambientes</span>
                        </h1>
                    </Animated>
                    <Animated>
                        <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
                            Mais de 40 anos selecionando os <strong className="text-white">melhores produtos das marcas mais confiáveis</strong> do mercado. 
                            Qualidade, durabilidade e o melhor custo-benefício para seu projeto.
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <CTAButton icon={ArrowRight} className="text-lg px-8 py-4">
                            Solicitar Catálogo Completo
                        </CTAButton>
                        <a 
                            href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                            className="flex items-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold"
                        >
                            <Phone size={20} />
                            Falar com Especialista
                        </a>
                    </Animated>
                </div>
            </section>

            {/* Categorias em Destaque */}
            <section className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Explore Nossas Categorias
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Cada categoria foi cuidadosamente selecionada para atender diferentes necessidades e estilos. 
                            Descubra a solução perfeita para seu projeto.
                        </p>
                    </Animated>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {(activeCmsCategories && activeCmsCategories.length ? activeCmsCategories.map(c=>({
                            id: c.slug || c.id,
                            title: c.title,
                            subtitle: c.subtitle || productCategories.find(pc=>pc.id===(c.slug||c.id))?.subtitle,
                            description: c.description || productCategories.find(pc=>pc.id===(c.slug||c.id))?.description,
                            benefits: (c.benefits && c.benefits.length ? c.benefits : productCategories.find(pc=>pc.id===(c.slug||c.id))?.benefits) || [],
                            idealFor: c.idealFor || productCategories.find(pc=>pc.id===(c.slug||c.id))?.idealFor,
                            image: c.image || productCategories.find(pc=>pc.id===(c.slug||c.id))?.image,
                          })) : productCategories).map((category) => (
                            <Animated key={category.id} className="group cursor-pointer" onClick={() => handleFilterChange(category.id)}>
                                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                                    <div className="relative h-64 overflow-hidden">
                                        <img 
                                            src={getOptimizedImg(category.image, 800)} 
                                            alt={category.title}
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                                        <div className="absolute bottom-4 left-4 right-4 text-white">
                                            <h3 className="text-2xl font-bold font-title mb-1">{category.title}</h3>
                                            {category.subtitle && <p className="text-[#D67441] font-semibold text-sm">{category.subtitle}</p>}
                                        </div>
                                        <div className="absolute top-4 right-4 bg-[#D67441] text-white px-3 py-1 rounded-full text-sm font-bold">
                                            {filters.find(f => f.id === category.id)?.count || 0} produtos
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        {category.description && <p className="text-[#4A4A4A] mb-4 leading-relaxed">{category.description}</p>}
                                        {(category.benefits && category.benefits.length > 0) && (
                                          <div className="mb-4">
                                            <h4 className="font-semibold text-[#2C5F5D] mb-2 text-sm">Principais Benefícios:</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {category.benefits.slice(0, 2).map((benefit, idx) => (
                                                    <span key={idx} className="bg-[#D67441]/10 text-[#D67441] px-3 py-1 rounded-full text-xs font-medium">
                                                        {benefit}
                                                    </span>
                                                ))}
                                            </div>
                                          </div>
                                        )}
                                        {(category.idealFor) && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-[#4A4A4A]"><strong>Ideal para:</strong> {category.idealFor}</span>
                                            <div className="text-[#D67441] font-semibold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-1">
                                                Ver Produtos <ArrowRight size={14} />
                                            </div>
                                          </div>
                                        )}
                                    </div>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* Filtros Aprimorados com Scroll Target */}
            <section className="py-20 bg-white" ref={productsGridRef}>
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Nosso Catálogo Completo
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Explore nossa linha completa de produtos, cuidadosamente selecionados das melhores marcas do mercado.
                        </p>
                    </Animated>

                    {/* Filtros Desktop */}
                    <div className="hidden md:flex justify-center flex-wrap gap-3 mb-12">
                        {filters.map(f => ( 
                            <button 
                                key={f.id} 
                                onClick={() => handleFilterChange(f.id)} 
                                className={cl(
                                    'flex items-center gap-2 py-3 px-6 rounded-full font-semibold transition-all duration-300 border-2 transform hover:scale-105',
                                    filter === f.id 
                                        ? 'bg-[#2C5F5D] text-white border-[#2C5F5D] shadow-lg scale-105' 
                                        : 'bg-white text-[#4A4A4A] border-gray-200 hover:bg-gray-50 hover:border-[#2C5F5D] hover:shadow-md'
                                )}
                            >
                                {f.icon}
                                <span>{f.label}</span>
                                <span className={cl(
                                    'text-xs px-2 py-1 rounded-full transition-all duration-300',
                                    filter === f.id ? 'bg-white/20' : 'bg-[#D67441]/20 text-[#D67441]'
                                )}>
                                    {f.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Filtros Mobile */}
                    <div className="md:hidden mb-8">
                        <div className="grid grid-cols-2 gap-3 max-w-lg mx-auto">
                            {filters.map(f => ( 
                                <button 
                                    key={f.id} 
                                    onClick={() => handleFilterChange(f.id)} 
                                    className={cl(
                                        'flex flex-col items-center justify-center gap-2 py-4 px-3 rounded-xl font-semibold transition-all duration-300 border-2 min-h-[90px] transform',
                                        filter === f.id 
                                            ? 'bg-[#2C5F5D] text-white border-[#2C5F5D] shadow-lg scale-105' 
                                            : 'bg-white text-[#4A4A4A] border-gray-200 hover:bg-gray-50 hover:border-[#2C5F5D] hover:shadow-md active:scale-95'
                                    )}
                                >
                                    <div className={cl(
                                        'p-2 rounded-lg transition-colors duration-300',
                                        filter === f.id 
                                            ? 'bg-white/20' 
                                            : 'bg-[#2C5F5D]/10'
                                    )}>
                                        {React.cloneElement(f.icon, { 
                                            className: filter === f.id ? 'text-white' : 'text-[#2C5F5D]' 
                                        })}
                                    </div>
                                    <span className="text-sm leading-tight text-center">{f.label}</span>
                                    <span className={cl(
                                        'text-xs px-2 py-1 rounded-full transition-all duration-300',
                                        filter === f.id ? 'bg-white/20' : 'bg-[#D67441]/20 text-[#D67441]'
                                    )}>
                                        {f.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                        
                        {/* Indicador do filtro ativo no mobile */}
                        <div className="mt-6 text-center">
                            <div className="inline-flex items-center gap-2 bg-[#F5F3F0] px-4 py-2 rounded-full">
                                <div className="w-2 h-2 bg-[#D67441] rounded-full animate-pulse"></div>
                                <p className="text-sm text-[#4A4A4A]">
                                    <span className="font-bold text-[#2C5F5D]">
                                        {filters.find(f => f.id === filter)?.label}
                                    </span>
                                    {filteredProducts.length > 0 && (
                                        <span className="ml-2 text-[#D67441]">
                                            • {filteredProducts.length} {filteredProducts.length === 1 ? 'produto' : 'produtos'}
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Loading indicator durante a mudança de filtro */}
                    <div className="mb-8">
                        <div className={cl(
                            "transition-all duration-300",
                            "opacity-100"
                        )}>
                            {/* Grid de Produtos Melhorado */}
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredProducts.map((product, index) => (
                                    <Animated 
                                        key={`${product.name}-${filter}`} 
                                        className="bg-white rounded-2xl shadow-lg overflow-hidden group border border-gray-100 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        <div className="overflow-hidden h-64 relative">
                                            <img 
                                                loading="lazy" 
                                                src={product.img} 
                                                alt={product.name} 
                                                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" 
                                            />
                                            <div className="absolute top-4 left-4">
                                                <span className="bg-[#D67441] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                                    {product.type === 'vinilico' ? 'Vinílico' : 
                                                     product.type === 'laminado' ? 'Laminado' : 
                                                     product.type === 'carpete' ? 'Carpete' : 
                                                     product.type === 'forro' ? 'Forro' : 'Rodapé'}
                                                </span>
                                            </div>
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                            <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                                                <p className="text-sm mb-2">Ver especificações completas</p>
                                                <div className="w-10 h-10 bg-[#D67441] rounded-full flex items-center justify-center">
                                                    <ArrowRight size={20} />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold text-[#2C5F5D] font-title mb-2 group-hover:text-[#D67441] transition-colors duration-300">
                                                {product.name}
                                            </h3>
                                            <p className="text-[#4A4A4A] text-sm mb-4 leading-relaxed">{product.description}</p>
                                            
                                            {/* Especificações em Grid */}
                                            <div className="grid grid-cols-2 gap-3 mb-6">
                                                <div className="bg-[#F5F3F0] p-3 rounded-lg text-center group-hover:bg-[#2C5F5D]/5 transition-colors duration-300">
                                                    <Award size={16} className="text-[#D67441] mx-auto mb-1"/>
                                                    <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Marcas</span>
                                                    <span className="text-xs text-gray-700 font-medium">{product.brand}</span>
                                                </div>
                                                <div className="bg-[#F5F3F0] p-3 rounded-lg text-center group-hover:bg-[#2C5F5D]/5 transition-colors duration-300">
                                                    <Ruler size={16} className="text-[#D67441] mx-auto mb-1"/>
                                                    <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Espessura</span>
                                                    <span className="text-xs text-gray-700 font-medium">{product.thickness}</span>
                                                </div>
                                                <div className="bg-[#F5F3F0] p-3 rounded-lg text-center group-hover:bg-[#2C5F5D]/5 transition-colors duration-300">
                                                    <Star size={16} className="text-[#D67441] mx-auto mb-1"/>
                                                    <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Diferencial</span>
                                                    <span className="text-xs text-gray-700 font-medium">{product.feature}</span>
                                                </div>
                                                <div className="bg-[#F5F3F0] p-3 rounded-lg text-center group-hover:bg-[#2C5F5D]/5 transition-colors duration-300">
                                                    <Home size={16} className="text-[#D67441] mx-auto mb-1"/>
                                                    <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Ambientes</span>
                                                    <span className="text-xs text-gray-700 font-medium">{product.environments}</span>
                                                </div>
                                            </div>
                                            
                                            <CTAButton className="w-full group-hover:bg-[#B85A35] transition-colors duration-300" icon={ArrowRight}>
                                                Solicitar Orçamento
                                            </CTAButton>
                                        </div>
                                    </Animated>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Mensagem quando não há produtos */}
                    {filteredProducts.length === 0 && (
                        <Animated className="text-center py-16">
                            <div className="text-6xl text-gray-300 mb-4">🔍</div>
                            <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-2">
                                Nenhum produto encontrado
                            </h3>
                            <p className="text-[#4A4A4A] mb-6">
                                Não encontramos produtos nesta categoria. Que tal explorar outras opções?
                            </p>
                            <CTAButton as="button" onClick={() => handleFilterChange('*')} variant="secondary">
                                Ver Todos os Produtos
                            </CTAButton>
                        </Animated>
                    )}
                </div>
            </section>
            {/* === PARCEIROS DE CONFIANÇA - UPGRADE COMPLETO === */}
<section className="py-20 bg-white">
    <div className="container mx-auto px-6">
         <Animated>
            <SectionTitle>Parceiros de Confiança</SectionTitle>
            <SectionSubtitle>Trabalhamos exclusivamente com as marcas mais respeitadas do mercado, garantindo qualidade e durabilidade em cada produto.</SectionSubtitle>
        </Animated>
    </div>
    <Animated className="mt-16 w-full inline-flex flex-nowrap overflow-hidden [mask-image:_linear-gradient(to_right,transparent_0,_black_128px,_black_calc(100%-128px),transparent_100%)]">
        <ul className="flex items-center justify-center md:justify-start [&_li]:mx-6 animate-infinite-scroll">
            {partnersData.map((partner, index) => (
                <li key={index}>
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 w-48 h-28 flex flex-col justify-center items-center text-center">
                        <h4 className="font-bold text-lg text-[#2C5F5D] font-title leading-tight mb-1">
                            {partner.main}
                        </h4>
                        <p className="text-sm text-[#4A4A4A] font-medium leading-tight">
                            {partner.sub}
                        </p>
                    </div>
                </li>
            ))}
        </ul>
        <ul className="flex items-center justify-center md:justify-start [&_li]:mx-6 animate-infinite-scroll" aria-hidden="true">
            {partnersData.map((partner, index) => (
                <li key={`clone-${index}`}>
                    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 w-48 h-28 flex flex-col justify-center items-center text-center">
                        <h4 className="font-bold text-lg text-[#2C5F5D] font-title leading-tight mb-1">
                            {partner.main}
                        </h4>
                        <p className="text-sm text-[#4A4A4A] font-medium leading-tight">
                            {partner.sub}
                        </p>
                    </div>
                </li>
            ))}
        </ul>
    </Animated>
</section>

            {/* Diferenciais do COMBO */}
            <section className="py-20 bg-[#2C5F5D] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold font-title mb-4">
                            Mais que Produtos: O COMBO Dsanzo
                        </h2>
                        <p className="text-xl text-gray-200 max-w-4xl mx-auto">
                            Não vendemos apenas materiais. Oferecemos a solução completa que elimina a dor de cabeça da sua reforma.
                        </p>
                    </Animated>
                    
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                icon: <Users size={32} />,
                                title: 'Consultoria Personalizada',
                                description: 'Visitamos seu espaço e desenvolvemos um projeto sob medida para suas necessidades.'
                            },
                            {
                                icon: <ShoppingBag size={32} />,
                                title: 'Todos os Materiais',
                                description: 'Fornecemos tudo: pisos, rodapés, colas, ferramentas. Você não precisa se preocupar com nada.'
                            },
                            {
                                icon: <Wrench size={32} />,
                                title: 'Instalação Própria',
                                description: 'Nossa equipe especializada realiza a instalação com perfeição e limpeza total.'
                            },
                            {
                                icon: <ShieldCheck size={32} />,
                                title: 'Garantia Total',
                                description: 'Garantimos tanto os produtos quanto nossa mão de obra. Sua tranquilidade é nossa prioridade.'
                            }
                        ].map((item, index) => (
                            <Animated key={index} className="text-center group">
                                <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-2 h-full">
                                    <div className="text-[#D67441] mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                                        {item.icon}
                                    </div>
                                    <h3 className="text-xl font-bold font-title mb-4 group-hover:text-[#D67441] transition-colors duration-300">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-200 leading-relaxed">{item.description}</p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Final Poderoso */}
            <section className="py-20 bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-[#3d7c78]/20 rounded-full"></div>
                    <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#1a3d3b]/30 rounded-full"></div>
                </div>
                
                <div className="relative container mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <Animated>
                            <h2 className="text-3xl md:text-5xl font-bold font-title mb-6">
                                Encontrou o Produto Ideal? 
                                <span className="block text-[#D67441]">Vamos Fazer Acontecer!</span>
                            </h2>
                            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                                Mais de 40 anos selecionando os melhores produtos para transformar ambientes. 
                                <strong className="text-white"> Solicite seu orçamento e descubra como é fácil reformar com o COMBO Dsanzo!</strong>
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4 mb-8">
                                <CTAButton icon={ArrowRight} className="text-lg px-8 py-4 bg-[#D67441] hover:bg-[#B85A35]">
                                    Solicitar Orçamento COMBO
                                </CTAButton>
                                <a 
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-6 py-3 rounded-lg hover:border-[#D67441]"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/>
                                    </svg>
                                    Falar com Especialista
                                </a>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-6 text-center">
                                {[
                                    { icon: <Award size={24} />, text: 'Produtos Premium' },
                                    { icon: <ShieldCheck size={24} />, text: 'Garantia Total' },
                                    { icon: <Users size={24} />, text: 'Equipe Própria' }
                                ].map((item, index) => (
                                    <div key={index} className="flex flex-col items-center gap-2">
                                        <div className="text-[#D67441]">{item.icon}</div>
                                        <span className="text-sm font-semibold">{item.text}</span>
                                    </div>
                                ))}
                            </div>
                        </Animated>
                        
                        <Animated className="relative" id="form-corporativo">
                            <div className="bg-white p-8 rounded-2xl shadow-2xl">
                                <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-6 text-center">
                                    Solicite Seu Orçamento Gratuito
                                </h3>
                                <form name="corporativo-orcamento" method="POST" data-netlify="true" action="/" className="space-y-4">
                                    <input type="hidden" name="form-name" value="corporativo-orcamento" />
                                    <p className="hidden"><label>Não preencha: <input name="bot-field" /></label></p>
                                    <div>
                                        <input 
                                            type="text" 
                                            name="name"
                                            placeholder="Seu nome completo" 
                                            className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] text-gray-800"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <input 
                                            type="tel" 
                                            name="phone"
                                            placeholder="Seu WhatsApp" 
                                            className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] text-gray-800"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <select name="product" className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] text-gray-800">
                                            <option value="">Produto de interesse</option>
                                            <option value="vinilico">Pisos Vinílicos</option>
                                            <option value="laminado">Pisos Laminados</option>
                                            <option value="carpete">Carpetes</option>
                                            <option value="forro">Forros</option>
                                            <option value="rodape">Rodapés</option>
                                            <option value="combo">COMBO Completo</option>
                                        </select>
                                    </div>
                                    <div>
                                        <textarea 
                                            name="message"
                                            placeholder="Conte sobre seu projeto..." 
                                            rows="3"
                                            className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] text-gray-800"
                                            required
                                        ></textarea>
                                    </div>
                                    <CTAButton as="button" type="submit" className="w-full text-lg py-4" icon={ArrowRight}>
                                        Enviar Solicitação
                                    </CTAButton>
                                </form>
                                <p className="text-xs text-gray-500 text-center mt-4">
                                    Resposta em até 2 horas • Atendimento especializado
                                </p>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>
        </div>
    );
};



// SERVICES PAGE - ALIGNMENT PERFECTLY FIXED
const ServicesPage = () => {
    const [openFAQ, setOpenFAQ] = useState(null);
    const [selectedService, setSelectedService] = useState('combo');
    
    const toggleFAQ = (index) => { setOpenFAQ(openFAQ === index ? null : index); };

    const serviceOptions = [
        {
            id: 'combo',
            title: 'DK DKOR',
            subtitle: 'COMBO Completo para o Cliente Final',
            icon: <Building size={32} className="text-[#D67441]" />,
            description: 'A solução completa para quem quer reformar sem dor de cabeça. Projeto + Material + Instalação + Garantia tudo em um só lugar.',
            features: ['Consultoria e projeto personalizado', 'Seleção de todos os materiais', 'Instalação com equipe própria', 'Garantia total em produtos e serviços', 'Limpeza e finalização completa'],
            ideal: 'Ideal para: Clientes residenciais e comerciais que buscam praticidade e tranquilidade',
            process: 'Consulta → Projeto → Instalação → Entrega com Garantia',
            highlight: 'Mais Popular'
        },
        {
            id: 'atacado',
            title: 'Levs Revest',
            subtitle: 'Distribuidora Atacadista',
            icon: <Briefcase size={32} className="text-[#D67441]" />,
            description: 'Fornecimento de materiais premium para instaladores, arquitetos, construtoras e lojistas com preços especiais e suporte técnico.',
            features: ['Preços especiais no atacado', 'Suporte técnico especializado', 'Treinamento gratuito para instaladores', 'Entrega programada', 'Consultoria técnica completa'],
            ideal: 'Ideal para: Instaladores, arquitetos, construtoras e revendedores',
            process: 'Cadastro → Pedido → Entrega → Suporte Contínuo',
            highlight: 'Para Profissionais'
        },
        {
            id: 'consultoria',
            title: 'Consultoria Especializada',
            subtitle: 'Orientação técnica completa',
            icon: <Lightbulb size={32} className="text-[#D67441]" />,
            description: 'Assessoria técnica para escolha de materiais, planejamento de projetos e soluções personalizadas para casos complexos.',
            features: ['Análise técnica do ambiente', 'Recomendação de produtos ideais', 'Cálculo preciso de materiais', 'Orientação de instalação', 'Suporte pós-projeto'],
            ideal: 'Ideal para: Projetos complexos que precisam de orientação técnica especializada',
            process: 'Avaliação → Análise → Recomendações → Acompanhamento',
            highlight: 'Expertise Técnica'
        }
    ];

    return (
        <div className="animate-fade-in bg-white">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-br from-[#2C5F5D] via-[#1e4442] to-[#0f2221] text-white py-32 overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#3d7c78]/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-[#1a3d3b]/40 to-transparent"></div>
                
                <div className="relative container mx-auto px-6 text-center">
                    <Animated>
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4">
                            Nossos Serviços
                        </span>
                    </Animated>
                    <Animated>
                        <h1 className="font-title text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                            Sua reforma, <span className="text-[#D67441]">sem complicação</span>
                        </h1>
                    </Animated>
                    <Animated>
                        <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
                            Há mais de 40 anos transformamos ambientes com soluções completas. 
                            <strong className="text-white"> Do projeto à garantia, cuidamos de tudo para você.</strong>
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <CTAButton icon={ArrowRight} className="text-lg px-8 py-4">
                            Solicitar Orçamento Grátis
                        </CTAButton>
                        <a 
                            href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                            className="flex items-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold"
                        >
                            <Phone size={20} />
                            {CONTACT_INFO.phone}
                        </a>
                    </Animated>
                </div>
            </div>

            {/* Seletor de Serviços */}
            <div className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Escolha a Solução Ideal para Você
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                            Cada cliente tem necessidades únicas. Conheça nossas modalidades de atendimento.
                        </p>
                    </Animated>

                    {/* Tabs de Serviços */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-6xl mx-auto">
                        {serviceOptions.map((service) => (
                            <button
                                key={service.id}
                                onClick={() => setSelectedService(service.id)}
                                className={cl(
                                    'p-6 rounded-xl border-2 transition-all duration-300 text-left relative overflow-hidden min-h-[220px] flex flex-col',
                                    selectedService === service.id
                                        ? 'bg-white border-[#D67441] shadow-lg transform scale-105'
                                        : 'bg-white/50 border-gray-200 hover:border-[#2C5F5D] hover:shadow-md'
                                )}
                            >
                                {service.highlight && (
                                    <span className="absolute top-4 right-4 bg-[#D67441] text-white text-xs font-bold px-3 py-1 rounded-full z-10">
                                        {service.highlight}
                                    </span>
                                )}
                                
                                <div className="mb-4">
                                    {service.icon}
                                </div>
                                
                                <div className="flex-grow flex flex-col">
                                    <h3 className="font-bold text-xl text-[#2C5F5D] font-title mb-2 pr-16 leading-tight">
                                        {service.title}
                                    </h3>
                                    <p className="text-sm text-[#D67441] font-semibold mb-3">
                                        {service.subtitle}
                                    </p>
                                    <p className="text-sm text-[#4A4A4A] leading-relaxed flex-grow">
                                        {service.description.substring(0, 120)}...
                                    </p>
                                </div>
                                
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <span className={cl(
                                        "text-xs font-semibold transition-colors",
                                        selectedService === service.id 
                                            ? "text-[#D67441]" 
                                            : "text-gray-500"
                                    )}>
                                        {selectedService === service.id ? "✓ Selecionado" : "Clique para ver detalhes"}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Conteúdo do Serviço Selecionado - ALINHAMENTO FINAL CORRETO */}
                    {serviceOptions.map((service) => (
                        selectedService === service.id && (
                            <Animated key={service.id} className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                                <div className="max-w-6xl mx-auto">
                                    {/* Header do Serviço */}
                                    <div className="text-center mb-12">
                                        <div className="flex justify-center mb-6">
                                            <div className="w-20 h-20 bg-gradient-to-br from-[#2C5F5D] to-[#1a3d3b] rounded-2xl flex items-center justify-center shadow-lg">
                                                {React.cloneElement(service.icon, { size: 40, className: "text-white" })}
                                            </div>
                                        </div>
                                        <h3 className="text-4xl font-bold text-[#2C5F5D] font-title mb-2">{service.title}</h3>
                                        <p className="text-xl text-[#D67441] font-semibold mb-4">{service.subtitle}</p>
                                        <p className="text-lg text-[#4A4A4A] max-w-4xl mx-auto leading-relaxed">{service.description}</p>
                                    </div>
                                    
                                    {/* Conteúdo em Grid - ALINHAMENTO COM ÚLTIMO ITEM */}
                                    <div className="grid md:grid-cols-2 gap-12">
                                        {/* Coluna Esquerda - Features */}
                                        <div>
                                            <h4 className="font-bold text-[#2C5F5D] mb-6 flex items-center gap-2 text-xl">
                                                <Star size={24} className="text-[#D67441]" />
                                                O que está incluído:
                                            </h4>
                                            <ul className="space-y-4">
                                                {service.features.map((feature, index) => (
                                                    <li key={index} className="flex items-center gap-4 text-[#4A4A4A] bg-[#F5F3F0] p-4 rounded-lg">
                                                        <div className="w-3 h-3 bg-[#D67441] rounded-full flex-shrink-0"></div>
                                                        <span className="font-medium">{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        
                                        {/* Coluna Direita - Informações Alinhadas com o ÚLTIMO ITEM */}
                                        <div className="flex flex-col">
                                            {/* MARGEM PARA ALINHAR COM O ÚLTIMO ITEM DA LISTA */}
                                            <div className="mt-16 space-y-6">
                                                <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 rounded-xl border border-gray-200">
                                                    <h5 className="font-bold text-[#2C5F5D] mb-3 flex items-center gap-2">
                                                        <Users size={20} className="text-[#D67441]" />
                                                        Para quem é ideal:
                                                    </h5>
                                                    <p className="text-[#4A4A4A] font-medium">{service.ideal.replace('Ideal para: ', '')}</p>
                                                </div>
                                                
                                                <div className="bg-gradient-to-br from-[#D67441]/5 to-[#D67441]/10 p-6 rounded-xl border border-[#D67441]/20">
                                                    <h5 className="font-bold text-[#2C5F5D] mb-3 flex items-center gap-2">
                                                        <ArrowRight size={20} className="text-[#D67441]" />
                                                        Como funciona:
                                                    </h5>
                                                    <p className="text-[#4A4A4A] font-medium">{service.process}</p>
                                                </div>
                                                
                                                <div>
                                                    <CTAButton icon={ArrowRight} className="w-full text-lg py-4">
                                                        Solicitar {service.title}
                                                    </CTAButton>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Animated>
                        )
                    ))}
                </div>
            </div>

            {/* Processo em 4 Etapas */}
            <div className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Como Funciona Nosso COMBO
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Um processo simples e transparente, desenvolvido ao longo de 40 anos para garantir sua total satisfação.
                        </p>
                    </Animated>

                    <div className="max-w-6xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                {
                                    step: '01',
                                    title: 'Consultoria',
                                    description: 'Visitamos seu espaço, ouvimos suas ideias e desenvolvemos um projeto personalizado.',
                                    icon: <Users size={28} />,
                                    color: 'from-[#2C5F5D] to-[#1e4442]'
                                },
                                {
                                    step: '02',
                                    title: 'Materiais',
                                    description: 'Selecionamos os melhores produtos das marcas mais confiáveis do mercado.',
                                    icon: <Layers size={28} />,
                                    color: 'from-[#D67441] to-[#B85A35]'
                                },
                                {
                                    step: '03',
                                    title: 'Instalação',
                                    description: 'Nossa equipe própria realiza a instalação com precisão e cuidado.',
                                    icon: <Wrench size={28} />,
                                    color: 'from-[#2C5F5D] to-[#1e4442]'
                                },
                                {
                                    step: '04',
                                    title: 'Garantia',
                                    description: 'Entregamos com garantia total e suporte contínuo para sua tranquilidade.',
                                    icon: <ShieldCheck size={28} />,
                                    color: 'from-[#D67441] to-[#B85A35]'
                                }
                            ].map((item, index) => (
                                <Animated key={index} className="text-center">
                                    <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 h-full">
                                        <div className={cl(
                                            'w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white shadow-lg',
                                            item.color
                                        )}>
                                            {item.icon}
                                        </div>
                                        
                                        <div className="mb-4">
                                            <span className="inline-block bg-[#F5F3F0] text-[#2C5F5D] font-bold px-3 py-1 rounded-full text-sm">
                                                Etapa {item.step}
                                            </span>
                                        </div>
                                        
                                        <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-4">{item.title}</h3>
                                        <p className="text-[#4A4A4A] leading-relaxed">{item.description}</p>
                                    </div>
                                </Animated>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Comparativo de Soluções */}
            <div className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Por Que Escolher o Grupo Dsanzo?
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Compare nossa solução completa com a abordagem tradicional do mercado.
                        </p>
                    </Animated>

                    <div className="max-w-5xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-8">
                            {/* Solução Tradicional */}
                            <Animated className="bg-white p-8 rounded-2xl shadow-lg border-l-4 border-gray-300">
                                <div className="text-center mb-6">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <X size={32} className="text-gray-400" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-600 font-title">Forma Tradicional</h3>
                                    <p className="text-gray-500">Como normalmente funciona</p>
                                </div>
                                <ul className="space-y-3">
                                    {[
                                        'Você precisa pesquisar fornecedores',
                                        'Coordenar diferentes profissionais',
                                        'Gerenciar prazos e entregas',
                                        'Lidar com problemas sozinho',
                                        'Garantias separadas e limitadas',
                                        'Estresse e perda de tempo'
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-center gap-3 text-gray-600">
                                            <X size={16} className="text-red-400 flex-shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </Animated>

                            {/* Nossa Solução */}
                            <Animated className="bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] p-8 rounded-2xl shadow-xl border-l-4 border-[#D67441] text-white relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-[#3d7c78]/20 rounded-full -translate-y-16 translate-x-16"></div>
                                <div className="text-center mb-6 relative z-10">
                                    <div className="w-16 h-16 bg-[#D67441] rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Star size={32} className="text-white" />
                                    </div>
                                    <h3 className="text-2xl font-bold font-title">COMBO Dsanzo</h3>
                                    <p className="text-gray-200">Nossa solução completa</p>
                                </div>
                                <ul className="space-y-3 relative z-10">
                                    {[
                                        'Tudo em um só lugar - sem complicação',
                                        'Equipe própria e especializada',
                                        'Cronograma garantido e cumprido',
                                        'Suporte completo do início ao fim',
                                        'Garantia total em produtos e serviços',
                                        'Tranquilidade e resultado garantido'
                                    ].map((item, index) => (
                                        <li key={index} className="flex items-center gap-3">
                                            <div className="w-5 h-5 bg-[#D67441] rounded-full flex items-center justify-center flex-shrink-0">
                                                <Star size={12} className="text-white" />
                                            </div>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </Animated>
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ */}
            <div className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Perguntas Frequentes
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                            Esclarecemos as principais dúvidas sobre nossos serviços e processo.
                        </p>
                    </Animated>
                    
                    <div className="max-w-4xl mx-auto">
                        <div className="bg-[#F5F3F0] rounded-2xl p-8">
                            {faqData.map((faq, index) => (
                                <AccordionItem 
                                    key={index} 
                                    q={faq.question} 
                                    a={faq.answer} 
                                    isOpen={openFAQ === index} 
                                    onClick={() => toggleFAQ(index)} 
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Final */}
            <div className="py-20 bg-gradient-to-br from-[#2C5F5D] via-[#1e4442] to-[#0f2221] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-[#3d7c78]/20 rounded-full"></div>
                    <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#1a3d3b]/30 rounded-full"></div>
                </div>
                
                <div className="relative container mx-auto px-6 text-center">
                    <Animated>
                        <h2 className="text-3xl md:text-5xl font-bold font-title mb-6">
                            Pronto para Transformar <br className="hidden md:block" />
                            <span className="text-[#D67441]">Seu Ambiente?</span>
                        </h2>
                    </Animated>
                    <Animated>
                        <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
                            Mais de 40 anos de experiência, milhares de clientes satisfeitos e a garantia de um trabalho perfeito. 
                            <strong className="text-white">Solicite seu orçamento gratuito agora!</strong>
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <CTAButton icon={ArrowRight} className="text-lg px-8 py-4 bg-[#D67441] hover:bg-[#B85A35]">
                            Quero Meu Orçamento Grátis
                        </CTAButton>
                        <a 
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-6 py-3 rounded-lg hover:border-[#D67441]"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/>
                            </svg>
                            WhatsApp Direto
                        </a>
                    </Animated>
                    
                    <Animated className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        {[
                            { icon: <Users size={24} />, text: '40+ Anos de Experiência' },
                            { icon: <Star size={24} />, text: 'Milhares de Clientes Satisfeitos' },
                            { icon: <ShieldCheck size={24} />, text: 'Garantia Total Inclusa' }
                        ].map((item, index) => (
                            <div key={index} className="flex items-center gap-3 justify-center text-gray-200">
                                <div className="text-[#D67441]">{item.icon}</div>
                                <span className="font-semibold">{item.text}</span>
                            </div>
                        ))}
                    </Animated>
                </div>
            </div>
        </div>
    );
};


// PORTFOLIO PAGE - COMPLETELY REDESIGNED
const PortfolioPage = () => {
    const [filter, setFilter] = useState('*');
    const [lightboxItem, setLightboxItem] = useState(null);
    const [hoveredItem, setHoveredItem] = useState(null);
    const { portfolio } = useProducts();
    
    const filters = [
        { id: '*', label: 'Todos os Projetos', icon: <ShoppingBag size={16} /> },
        { id: 'Residencial', label: 'Residencial', icon: <Home size={16} /> },
        { id: 'Comercial', label: 'Comercial', icon: <Building size={16} /> }
    ];
    
    // Usar apenas projetos do Firebase (já que os exemplos foram importados)
    const activeProjects = portfolio.filter(p => p.active !== false);
    const filteredItems = filter === '*' ? activeProjects : activeProjects.filter(p => p.category === filter);
    
    const stats = [
        { number: '500+', label: 'Projetos Realizados', icon: <Award size={24} /> },
        { number: '40+', label: 'Anos de Experiência', icon: <Star size={24} /> },
        { number: '98%', label: 'Clientes Satisfeitos', icon: <Users size={24} /> },
        { number: '100%', label: 'Garantia Total', icon: <ShieldCheck size={24} /> }
    ];

    return (
        <div className="animate-fade-in bg-white">
            {lightboxItem && <Lightbox item={lightboxItem} onClose={() => setLightboxItem(null)} />}
            
            {/* Hero Section Reformulada */}
            <div className="relative bg-gradient-to-br from-[#2C5F5D] via-[#1e4442] to-[#0f2221] text-white py-32 overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#3d7c78]/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-[#1a3d3b]/40 to-transparent"></div>
                
                <div className="relative container mx-auto px-6 text-center">
                    <Animated>
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4">
                            Nosso Portfólio
                        </span>
                    </Animated>
                    <Animated>
                        <h1 className="font-title text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                            Projetos que <span className="text-[#D67441]">Transformam Ambientes</span>
                        </h1>
                    </Animated>
                    <Animated>
                        <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
                            Há mais de 40 anos criamos ambientes únicos com nosso modelo COMBO. 
                            <strong className="text-white"> Veja na prática a qualidade e versatilidade das nossas soluções.</strong>
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <CTAButton icon={ArrowRight} className="text-lg px-8 py-4">
                            Quero um Projeto Assim
                        </CTAButton>
                        <a 
                            href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                            className="flex items-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold"
                        >
                            <Phone size={20} />
                            Fale com um Especialista
                        </a>
                    </Animated>
                </div>
            </div>

            {/* Estatísticas de Impacto */}
            <div className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Números que Falam por Si
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                            Cada projeto é uma história de transformação, qualidade e satisfação garantida.
                        </p>
                    </Animated>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <Animated key={index} className="text-center group">
                                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                                    <div className="text-[#D67441] mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                                        {stat.icon}
                                    </div>
                                    <div className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-2">
                                        {stat.number}
                                    </div>
                                    <p className="text-[#4A4A4A] font-medium">{stat.label}</p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </div>

            {/* Filtros Melhorados */}
            <div className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Explore Nossos Projetos
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            De residências aconchegantes a espaços comerciais sofisticados, cada projeto reflete nossa paixão por transformar ambientes.
                        </p>
                    </Animated>

                    {/* Filtros com Ícones */}
                    <div className="flex flex-wrap justify-center gap-4 mb-12">
                        {filters.map(f => (
                            <button 
                                key={f.id} 
                                onClick={() => setFilter(f.id)} 
                                className={cl(
                                    'flex items-center gap-2 py-3 px-6 rounded-full font-semibold transition-all duration-300 border-2',
                                    filter === f.id 
                                        ? 'bg-[#2C5F5D] text-white border-[#2C5F5D] shadow-lg transform scale-105' 
                                        : 'bg-white text-[#4A4A4A] border-gray-200 hover:bg-gray-50 hover:border-[#2C5F5D] hover:shadow-md'
                                )}
                            >
                                {f.icon}
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Grid de Projetos Melhorado */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredItems.map((item, index) => (
                            <Animated 
                                key={item.id} 
                                className="group cursor-pointer"
                                onMouseEnter={() => setHoveredItem(item.id)}
                                onMouseLeave={() => setHoveredItem(null)}
                                onClick={() => setLightboxItem(item)}
                            >
                                <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                                    <div className="relative overflow-hidden h-80">
                                        <img 
                                            loading="lazy" 
                                            src={item.gallery[0]} 
                                            alt={item.title} 
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700" 
                                        />
                                        
                                        {/* Overlay com informações */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                                            <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                                <span className="inline-block bg-[#D67441] text-white font-bold px-3 py-1 rounded-full text-xs uppercase tracking-wider mb-3">
                                                    {item.category}
                                                </span>
                                                <h3 className="text-2xl font-bold font-title mb-2">{item.title}</h3>
                                                <p className="text-gray-200 text-sm mb-4 line-clamp-2">{item.description}</p>
                                                
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <Layers size={16} className="text-[#D67441]" />
                                                        <span>{item.type}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[#D67441] font-semibold">
                                                        Ver Projeto <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform duration-300" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Badge de categoria sempre visível */}
                                        <div className="absolute top-4 left-4">
                                            <span className="bg-white/90 backdrop-blur-sm text-[#2C5F5D] font-bold px-3 py-1 rounded-full text-xs uppercase tracking-wider">
                                                {item.category}
                                            </span>
                                        </div>

                                        {/* Indicador de galeria */}
                                        {item.gallery.length > 1 && (
                                            <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-semibold">
                                                +{item.gallery.length - 1} fotos
                                            </div>
                                        )}
                                    </div>

                                    {/* Informações do card (sempre visíveis) */}
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-[#2C5F5D] font-title mb-2 group-hover:text-[#D67441] transition-colors duration-300">
                                            {item.title}
                                        </h3>
                                        <p className="text-[#4A4A4A] text-sm mb-4 line-clamp-2">{item.description}</p>
                                        
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                                                <Layers size={16} className="text-[#D67441]" />
                                                <span>{item.type}</span>
                                            </div>
                                            <div className="text-[#D67441] font-semibold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                Ver Detalhes →
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Animated>
                        ))}
                    </div>

                    {/* Mensagem quando não há projetos */}
                    {filteredItems.length === 0 && (
                        <Animated className="text-center py-16">
                            <div className="text-6xl text-gray-300 mb-4">🏗️</div>
                            <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-2">
                                Projetos em Breve
                            </h3>
                            <p className="text-[#4A4A4A] mb-6">
                                Estamos organizando nosso portfólio nesta categoria. Em breve você verá projetos incríveis aqui!
                            </p>
                            <CTAButton as="button" onClick={() => setFilter('*')} variant="secondary">
                                Ver Todos os Projetos
                            </CTAButton>
                        </Animated>
                    )}
                </div>
            </div>

            {/* Seção de Processo COMBO */}
            <div className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            O Segredo por Trás de Cada Projeto
                        </h2>
                        <p className="text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Nosso exclusivo modelo COMBO garante que cada projeto seja executado com perfeição, do início ao fim.
                        </p>
                    </Animated>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                step: '01',
                                title: 'Consultoria',
                                description: 'Visitamos seu espaço e desenvolvemos um projeto personalizado que harmonize com seu estilo.',
                                icon: <Users size={32} />,
                                color: 'from-[#2C5F5D] to-[#1e4442]'
                            },
                            {
                                step: '02',
                                title: 'Materiais',
                                description: 'Selecionamos os melhores produtos das marcas mais confiáveis do mercado.',
                                icon: <Layers size={32} />,
                                color: 'from-[#D67441] to-[#B85A35]'
                            },
                            {
                                step: '03',
                                title: 'Instalação',
                                description: 'Nossa equipe própria realiza a instalação com precisão e atenção aos detalhes.',
                                icon: <Wrench size={32} />,
                                color: 'from-[#2C5F5D] to-[#1e4442]'
                            },
                            {
                                step: '04',
                                title: 'Garantia',
                                description: 'Entregamos com garantia total e suporte contínuo para sua tranquilidade.',
                                icon: <ShieldCheck size={32} />,
                                color: 'from-[#D67441] to-[#B85A35]'
                            }
                        ].map((item, index) => (
                            <Animated key={index} className="text-center group">
                                <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                                    <div className={cl(
                                        'w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300',
                                        item.color
                                    )}>
                                        {item.icon}
                                    </div>
                                    
                                    <div className="mb-4">
                                        <span className="inline-block bg-[#F5F3F0] text-[#2C5F5D] font-bold px-3 py-1 rounded-full text-sm">
                                            Etapa {item.step}
                                        </span>
                                    </div>
                                    
                                    <h3 className="text-xl font-bold text-[#2C5F5D] font-title mb-4 group-hover:text-[#D67441] transition-colors duration-300">
                                        {item.title}
                                    </h3>
                                    <p className="text-[#4A4A4A] leading-relaxed">{item.description}</p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </div>

            {/* Depoimentos de Clientes */}
            <div className="py-20 bg-[#2C5F5D] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold font-title mb-4">
                            O Que Nossos Clientes Dizem
                        </h2>
                        <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                            A satisfação de quem confia em nosso trabalho é nossa maior conquista.
                        </p>
                    </Animated>
                    
                    <div className="grid lg:grid-cols-3 gap-8">
                        {testimonials.map((testimonial, index) => (
                            <Animated key={index} className="bg-white/10 p-8 rounded-2xl backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-all duration-300">
                                <div className="flex mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="text-[#D67441]" fill="#D67441" size={20} />
                                    ))}
                                </div>
                                <p className="text-gray-200 italic mb-6 text-lg leading-relaxed">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center">
                                    <img 
                                        loading="lazy" 
                                        src={testimonial.img} 
                                        alt={testimonial.author} 
                                        className="w-16 h-16 rounded-full object-cover mr-4 border-2 border-[#D67441]"
                                    />
                                    <div>
                                        <p className="font-bold text-lg text-white">{testimonial.author}</p>
                                        <p className="text-sm text-[#D67441] font-semibold">{testimonial.role}</p>
                                    </div>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Final Poderoso */}
            <div className="py-20 bg-gradient-to-br from-[#2C5F5D] via-[#1e4442] to-[#0f2221] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 left-0 w-full h-full">
                    <div className="absolute top-10 left-10 w-32 h-32 bg-[#3d7c78]/20 rounded-full"></div>
                    <div className="absolute bottom-10 right-10 w-48 h-48 bg-[#1a3d3b]/30 rounded-full"></div>
                </div>
                
                <div className="relative container mx-auto px-6 text-center">
                    <Animated>
                        <h2 className="text-3xl md:text-5xl font-bold font-title mb-6">
                            Pronto para Criar <br className="hidden md:block" />
                            <span className="text-[#D67441]">Seu Projeto dos Sonhos?</span>
                        </h2>
                    </Animated>
                    <Animated>
                        <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-8">
                            Cada projeto que você viu aqui começou com uma conversa. Que tal começarmos a sua? 
                            <strong className="text-white">Solicite uma consultoria gratuita agora!</strong>
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <CTAButton icon={ArrowRight} className="text-lg px-8 py-4 bg-[#D67441] hover:bg-[#B85A35]">
                            Quero Minha Consultoria Grátis
                        </CTAButton>
                        <a 
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-6 py-3 rounded-lg hover:border-[#D67441]"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/>
                            </svg>
                            WhatsApp Direto
                        </a>
                    </Animated>
                    
                    <Animated className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        {[
                            { icon: <Users size={24} />, text: 'Consultoria Gratuita' },
                            { icon: <Star size={24} />, text: 'Projeto Personalizado' },
                            { icon: <ShieldCheck size={24} />, text: 'Garantia Total Inclusa' }
                        ].map((item, index) => (
                            <div key={index} className="flex items-center gap-3 justify-center text-gray-200">
                                <div className="text-[#D67441]">{item.icon}</div>
                                <span className="font-semibold">{item.text}</span>
                            </div>
                        ))}
                    </Animated>
                </div>
            </div>
        </div>
    );
};


// CONTACT PAGE - DESKTOP FIXES AND MOBILE OPTIMIZED
const ContactPage = () => {
    const [formState, setFormState] = useState({ 
        name: '', 
        email: '', 
        phone: '', 
        service: '', 
        project: '', 
        message: '' 
    });
    const [status, setStatus] = useState({ submitting: false, success: false, error: false });
    
    const handleChange = (e) => { 
        setFormState({ ...formState, [e.target.name]: e.target.value }); 
    };
    
    const handleSubmit = async (e) => { 
        e.preventDefault(); 
        setStatus({ submitting: true, success: false, error: false }); 
        const form = e.target;
        const formData = new FormData(form);
        try {
            const response = await fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(formData).toString(),
            });
            if (response.ok) {
                setStatus({ submitting: false, success: true, error: false }); 
                setFormState({ name: '', email: '', phone: '', service: '', project: '', message: '' }); 
            } else {
                setStatus({ submitting: false, success: false, error: true }); 
            }
        } catch (err) {
            setStatus({ submitting: false, success: false, error: true }); 
        }
    };

    const contactMethods = [
        {
            icon: <Phone size={32} className="text-[#D67441]" />,
            title: "Fale Conosco",
            subtitle: "Atendimento direto com a família",
            info: CONTACT_INFO.phone,
            action: `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`,
            description: "Ligue agora e fale diretamente com Levy ou Úbya"
        },
        {
            icon: <svg width="32" height="32" viewBox="0 0 24 24" fill="#D67441"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/></svg>,
            title: "WhatsApp",
            subtitle: "Resposta rápida e prática",
            info: CONTACT_INFO.phone,
            action: WHATSAPP_URL,
            description: "Envie uma mensagem e receba resposta em minutos"
        },
        {
            icon: <Mail size={32} className="text-[#D67441]" />,
            title: "E-mail",
            subtitle: "Para projetos detalhados",
            info: "contato@grupodsanzo.com.br",
            action: "mailto:contato@grupodsanzo.com.br",
            description: "Envie detalhes do seu projeto por e-mail"
        },
        {
            icon: <MapPin size={32} className="text-[#D67441]" />,
            title: "Visite Nosso Showroom",
            subtitle: "Veja os produtos de perto",
            info: "Indaiatuba - SP",
            action: CONTACT_INFO.maps_url,
            description: "Conheça nossa linha completa pessoalmente"
        }
    ];

    const serviceTypes = [
        { value: 'combo', label: 'COMBO Completo (DK DKOR)' },
        { value: 'atacado', label: 'Compra no Atacado (Levs Revest)' },
        { value: 'consultoria', label: 'Consultoria Especializada' },
        { value: 'treinamento', label: 'Treinamento para Instaladores' },
        { value: 'outros', label: 'Outros Serviços' }
    ];

    const projectTypes = [
        { value: 'residencial', label: 'Residencial' },
        { value: 'comercial', label: 'Comercial' },
        { value: 'industrial', label: 'Industrial' },
        { value: 'reforma', label: 'Reforma' },
        { value: 'construcao', label: 'Construção Nova' }
    ];

    return (
        <div className="animate-fade-in bg-white">
            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] text-white py-24 md:py-32 overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#3d7c78]/30 to-transparent"></div>
                <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-tr from-[#1a3d3b]/40 to-transparent"></div>
                
                <div className="relative container mx-auto px-6 text-center">
                    <Animated>
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                            Fale Conosco • Desde 1980
                        </span>
                    </Animated>
                    <Animated>
                        <h1 className="font-title text-3xl md:text-6xl font-extrabold leading-tight mb-6">
                            Vamos Transformar 
                            <span className="block text-[#D67441]">Seu Projeto Juntos?</span>
                        </h1>
                    </Animated>
                    <Animated>
                        <p className="text-lg md:text-2xl text-gray-200 max-w-4xl mx-auto mb-8 leading-relaxed">
                            Há mais de 40 anos, a <strong className="text-white">família Dsanzo</strong> está pronta para ouvir suas ideias 
                            e transformá-las em realidade. Conte conosco para seu próximo projeto!
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <a 
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-[#25D366] text-white font-bold py-4 px-6 md:px-8 rounded-lg hover:bg-[#1da851] transition-all duration-300 transform hover:-translate-y-1 shadow-lg text-sm md:text-base"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/>
                            </svg>
                            Falar no WhatsApp Agora
                        </a>
                        <a 
                            href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                            className="flex items-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-4 md:px-6 py-3 rounded-lg hover:border-[#D67441] text-sm md:text-base"
                        >
                            <Phone size={18} />
                            {CONTACT_INFO.phone}
                        </a>
                    </Animated>
                </div>
            </section>

            {/* Formas de Contato - MARGEM CORRIGIDA E CORES AJUSTADAS */}
            <section className="py-16 md:py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-12 md:mb-16">
                        <h2 className="text-2xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Escolha a Melhor Forma de Nos Contatar
                        </h2>
                        <p className="text-base md:text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Estamos sempre disponíveis para atender você da forma mais conveniente. 
                            Escolha o canal que preferir e fale diretamente com nossa equipe.
                        </p>
                    </Animated>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {contactMethods.map((method, index) => (
                            <Animated key={index} className="group">
                                <a 
                                    href={method.action}
                                    target={method.action.startsWith('http') ? '_blank' : '_self'}
                                    rel={method.action.startsWith('http') ? 'noopener noreferrer' : ''}
                                    className="block bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center h-full"
                                >
                                    <div className="mb-4 md:mb-6 flex justify-center">
                                        <div className="w-16 h-16 md:w-20 md:h-20 bg-[#F5F3F0] rounded-2xl flex items-center justify-center group-hover:bg-[#2C5F5D] transition-colors duration-300">
                                            <div className="group-hover:scale-110 transition-transform duration-300 [&>svg]:group-hover:fill-white [&>*]:group-hover:text-white">
                                                {method.icon}
                                            </div>
                                        </div>
                                    </div>
                                    <h3 className="text-lg md:text-xl font-bold text-[#2C5F5D] font-title mb-2 group-hover:text-[#D67441] transition-colors duration-300">
                                        {method.title}
                                    </h3>
                                    <p className="text-[#D67441] font-semibold text-sm mb-3">
                                        {method.subtitle}
                                    </p>
                                    <p className="text-[#2C5F5D] font-bold mb-3 text-sm md:text-base">
                                        {method.info}
                                    </p>
                                    <p className="text-[#4A4A4A] text-xs md:text-sm">
                                        {method.description}
                                    </p>
                                </a>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* Formulário de Contato - MOBILE OTIMIZADO */}
            <section className="py-16 md:py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-start">
                        {/* Informações da Empresa - MOBILE OTIMIZADO */}
                        <Animated>
                            <div className="bg-[#2C5F5D] text-white p-6 md:p-8 lg:p-12 rounded-2xl order-2 lg:order-1">
                                <h3 className="text-2xl md:text-3xl font-bold font-title mb-4 md:mb-6">
                                    Conheça a Família Dsanzo
                                </h3>
                                <p className="text-gray-200 mb-6 md:mb-8 leading-relaxed text-sm md:text-base">
                                    Há mais de 40 anos, Levy e Úbya construíram mais que uma empresa: 
                                    criaram um legado de qualidade, confiança e atendimento familiar que 
                                    transforma ambientes e vidas.
                                </p>

                                {/* Informações de Contato - MOBILE OTIMIZADO */}
                                <div className="space-y-4 md:space-y-6">
                                    <div className="flex items-start gap-3 md:gap-4">
                                        <MapPin className="text-[#D67441] flex-shrink-0 mt-1" size={20} />
                                        <div>
                                            <p className="font-bold text-base md:text-lg mb-1">Nosso Showroom</p>
                                            <p className="text-gray-200 text-sm md:text-base">{CONTACT_INFO.address}</p>
                                            <a 
                                                href={CONTACT_INFO.maps_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-[#D67441] hover:text-white transition-colors text-xs md:text-sm font-semibold"
                                            >
                                                Ver no Google Maps →
                                            </a>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3 md:gap-4">
                                        <Phone className="text-[#D67441] flex-shrink-0 mt-1" size={20} />
                                        <div>
                                            <p className="font-bold text-base md:text-lg mb-1">Telefone & WhatsApp</p>
                                            <p className="text-gray-200 text-sm md:text-base">{CONTACT_INFO.phone}</p>
                                            <p className="text-xs md:text-sm text-gray-300">Atendimento direto com a família</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-3 md:gap-4">
                                        <Mail className="text-[#D67441] flex-shrink-0 mt-1" size={20} />
                                        <div>
                                            <p className="font-bold text-base md:text-lg mb-1">E-mail</p>
                                            <p className="text-gray-200 text-sm md:text-base">contato@grupodsanzo.com.br</p>
                                            <p className="text-xs md:text-sm text-gray-300">Para projetos detalhados</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Horário de Funcionamento - MOBILE OTIMIZADO */}
                                <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-white/20">
                                    <h4 className="font-bold text-base md:text-lg mb-3 md:mb-4 flex items-center gap-2">
                                        <Building size={18} className="text-[#D67441]" />
                                        Horário de Atendimento
                                    </h4>
                                    <div className="space-y-1 md:space-y-2 text-xs md:text-sm">
                                        {CONTACT_INFO.hours.map((hour, index) => (
                                            <div key={index} className="flex justify-between">
                                                <span className="text-gray-200">{hour.day}:</span>
                                                <span className="text-white font-semibold">{hour.time}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Redes Sociais - MOBILE OTIMIZADO */}
                                <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-white/20">
                                    <h4 className="font-bold text-base md:text-lg mb-3 md:mb-4">Nos Siga nas Redes</h4>
                                    <div className="flex gap-3 md:gap-4">
                                        <a href="#" className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D67441] transition-colors">
                                            <Instagram size={18} />
                                        </a>
                                        <a href="#" className="w-10 h-10 md:w-12 md:h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#D67441] transition-colors">
                                            <Facebook size={18} />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </Animated>

                        {/* Formulário - MOBILE OTIMIZADO */}
                        <Animated>
                            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl border border-gray-100 order-1 lg:order-2">
                                <h3 className="text-2xl md:text-3xl font-bold text-[#2C5F5D] font-title mb-4 md:mb-6">
                                    Solicite Seu Orçamento Gratuito
                                </h3>
                                <p className="text-[#4A4A4A] mb-6 md:mb-8 text-sm md:text-base">
                                    Preencha o formulário abaixo e nossa equipe entrará em contato em até 2 horas 
                                    para entender melhor seu projeto e oferecer a melhor solução.
                                </p>

                                <form name="contato" method="POST" data-netlify="true" action="/" onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                                    <input type="hidden" name="form-name" value="contato" />
                                    <p className="hidden"><label>Não preencha: <input name="bot-field" /></label></p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="name" className="block text-[#2C5F5D] mb-2 font-semibold text-sm md:text-base">
                                                Nome Completo *
                                            </label>
                                            <input 
                                                type="text" 
                                                id="name" 
                                                name="name" 
                                                value={formState.name} 
                                                onChange={handleChange} 
                                                className="w-full p-3 md:p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] focus:border-transparent transition-all duration-300 text-sm md:text-base" 
                                                placeholder="Seu nome completo"
                                                required 
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="phone" className="block text-[#2C5F5D] mb-2 font-semibold text-sm md:text-base">
                                                WhatsApp *
                                            </label>
                                            <input 
                                                type="tel" 
                                                id="phone" 
                                                name="phone" 
                                                value={formState.phone} 
                                                onChange={handleChange} 
                                                className="w-full p-3 md:p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] focus:border-transparent transition-all duration-300 text-sm md:text-base" 
                                                placeholder="(00) 00000-0000"
                                                required 
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className="block text-[#2C5F5D] mb-2 font-semibold text-sm md:text-base">
                                            E-mail *
                                        </label>
                                        <input 
                                            type="email" 
                                            id="email" 
                                            name="email" 
                                            value={formState.email} 
                                            onChange={handleChange} 
                                            className="w-full p-3 md:p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] focus:border-transparent transition-all duration-300 text-sm md:text-base" 
                                            placeholder="seu@email.com"
                                            required 
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="service" className="block text-[#2C5F5D] mb-2 font-semibold text-sm md:text-base">
                                                Tipo de Serviço
                                            </label>
                                            <select 
                                                id="service" 
                                                name="service" 
                                                value={formState.service} 
                                                onChange={handleChange} 
                                                className="w-full p-3 md:p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                                            >
                                                <option value="">Selecione um serviço</option>
                                                {serviceTypes.map((service) => (
                                                    <option key={service.value} value={service.value}>
                                                        {service.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label htmlFor="project" className="block text-[#2C5F5D] mb-2 font-semibold text-sm md:text-base">
                                                Tipo de Projeto
                                            </label>
                                            <select 
                                                id="project" 
                                                name="project" 
                                                value={formState.project} 
                                                onChange={handleChange} 
                                                className="w-full p-3 md:p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] focus:border-transparent transition-all duration-300 text-sm md:text-base"
                                            >
                                                <option value="">Selecione o tipo</option>
                                                {projectTypes.map((project) => (
                                                    <option key={project.value} value={project.value}>
                                                        {project.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="message" className="block text-[#2C5F5D] mb-2 font-semibold text-sm md:text-base">
                                            Conte sobre seu projeto *
                                        </label>
                                        <textarea 
                                            id="message" 
                                            name="message" 
                                            rows="4" 
                                            value={formState.message} 
                                            onChange={handleChange} 
                                            className="w-full p-3 md:p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D67441] focus:border-transparent transition-all duration-300 text-sm md:text-base" 
                                            placeholder="Descreva seu projeto, ambiente, metragem, preferências de materiais, prazo desejado..."
                                            required
                                        ></textarea>
                                    </div>

                                    <CTAButton 
                                        as="button" 
                                        type="submit"
                                        className="w-full text-base md:text-lg py-3 md:py-4" 
                                        icon={ArrowRight}
                                        disabled={status.submitting}
                                    >
                                        {status.submitting ? 'Enviando...' : 'Enviar Solicitação'}
                                    </CTAButton>

                                    {status.success && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                            <p className="text-green-800 font-semibold text-sm md:text-base">✅ Mensagem enviada com sucesso!</p>
                                            <p className="text-green-600 text-xs md:text-sm mt-1">
                                                Nossa equipe entrará em contato em até 2 horas. Obrigado!
                                            </p>
                                        </div>
                                    )}
                                    
                                    {status.error && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                                            <p className="text-red-800 font-semibold text-sm md:text-base">❌ Erro ao enviar mensagem</p>
                                            <p className="text-red-600 text-xs md:text-sm mt-1">
                                                Por favor, preencha todos os campos obrigatórios.
                                            </p>
                                        </div>
                                    )}
                                </form>

                                <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-gray-200 text-center">
                                    <p className="text-xs md:text-sm text-[#4A4A4A]">
                                        <strong>Garantimos:</strong> Resposta em até 2 horas • Atendimento personalizado • Orçamento sem compromisso
                                    </p>
                                </div>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* Mapa Interativo - MOBILE OTIMIZADO */}
            <section className="py-16 md:py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-12 md:mb-16">
                        <h2 className="text-2xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Visite Nosso Showroom
                        </h2>
                        <p className="text-base md:text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Conheça pessoalmente nossa linha completa de produtos e sinta a qualidade 
                            que oferecemos. Nossa equipe está pronta para recebê-lo!
                        </p>
                    </Animated>

                    <div className="grid lg:grid-cols-3 gap-6 md:gap-8 items-start">
                        <Animated className="lg:col-span-2 order-2 lg:order-1">
                            <div className="rounded-2xl overflow-hidden shadow-xl h-64 md:h-96">
                                <iframe 
                                    src={CONTACT_INFO.maps_embed_url} 
                                    width="100%" 
                                    height="100%" 
                                    style={{ border: 0 }} 
                                    allowFullScreen="" 
                                    loading="lazy" 
                                    referrerPolicy="no-referrer-when-downgrade" 
                                    title="Localização do Grupo Dsanzo"
                                ></iframe>
                            </div>
                        </Animated>

                        <Animated className="order-1 lg:order-2">
                            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl h-full flex flex-col justify-center">
                                <div className="text-center mb-4 md:mb-6">
                                    <div className="w-12 h-12 md:w-16 md:h-16 bg-[#D67441] rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4">
                                        <MapPin size={24} className="text-white" />
                                    </div>
                                    <h3 className="text-xl md:text-2xl font-bold text-[#2C5F5D] font-title mb-2">
                                        Como Chegar
                                    </h3>
                                </div>
                                
                                <div className="space-y-4 text-center">
                                    <p className="text-[#4A4A4A] text-sm md:text-base">
                                        <strong className="text-[#2C5F5D]">Endereço:</strong><br />
                                        {CONTACT_INFO.address}
                                    </p>
                                    
                                    <div className="flex flex-col gap-3">
                                        <a 
                                            href={CONTACT_INFO.maps_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-[#2C5F5D] text-white font-bold py-3 px-4 md:px-6 rounded-lg hover:bg-[#1e4442] transition-colors text-sm md:text-base"
                                        >
                                            Ver Rota no Maps
                                        </a>
                                        <a 
                                            href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                                            className="border-2 border-[#D67441] text-[#D67441] font-bold py-3 px-4 md:px-6 rounded-lg hover:bg-[#D67441] hover:text-white transition-colors text-sm md:text-base"
                                        >
                                            Ligar Antes de Ir
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* FAQ de Contato - MOBILE OTIMIZADO */}
            <section className="py-16 md:py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-12 md:mb-16">
                        <h2 className="text-2xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-4">
                            Dúvidas Frequentes sobre Atendimento
                        </h2>
                        <p className="text-base md:text-lg text-[#4A4A4A] max-w-3xl mx-auto">
                            Esclarecemos as principais dúvidas sobre nosso atendimento e processo de contato.
                        </p>
                    </Animated>

                    <div className="max-w-4xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                            <Animated className="space-y-4 md:space-y-6">
                                <div className="bg-[#F5F3F0] p-4 md:p-6 rounded-xl">
                                    <h3 className="font-bold text-[#2C5F5D] mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                                        <Users size={18} className="text-[#D67441]" />
                                        Quem me atende?
                                    </h3>
                                    <p className="text-[#4A4A4A] text-xs md:text-sm">
                                        Você será atendido diretamente pela família Dsanzo ou por nossa equipe 
                                        treinada pessoalmente por Levy e Úbya. Garantimos atendimento personalizado e humanizado.
                                    </p>
                                </div>

                                <div className="bg-[#F5F3F0] p-4 md:p-6 rounded-xl">
                                    <h3 className="font-bold text-[#2C5F5D] mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                                        <Building size={18} className="text-[#D67441]" />
                                        Posso visitar o showroom?
                                    </h3>
                                    <p className="text-[#4A4A4A] text-xs md:text-sm">
                                        Claro! Nosso showroom em Indaiatuba está aberto para visitação. 
                                        Recomendamos ligar antes para garantir que teremos alguém disponível para te atender com exclusividade.
                                    </p>
                                </div>

                                <div className="bg-[#F5F3F0] p-4 md:p-6 rounded-xl">
                                    <h3 className="font-bold text-[#2C5F5D] mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                                        <ShieldCheck size={18} className="text-[#D67441]" />
                                        O orçamento é gratuito?
                                    </h3>
                                    <p className="text-[#4A4A4A] text-xs md:text-sm">
                                        Sim! Todos os orçamentos são gratuitos e sem compromisso. 
                                        Fazemos questão de entender seu projeto antes de apresentar qualquer proposta.
                                    </p>
                                </div>
                            </Animated>

                            <Animated className="space-y-4 md:space-y-6">
                                <div className="bg-[#F5F3F0] p-4 md:p-6 rounded-xl">
                                    <h3 className="font-bold text-[#2C5F5D] mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                                        <Star size={18} className="text-[#D67441]" />
                                        Quanto tempo para resposta?
                                    </h3>
                                    <p className="text-[#4A4A4A] text-xs md:text-sm">
                                        Garantimos resposta em até 2 horas durante horário comercial. 
                                        Pelo WhatsApp, muitas vezes respondemos em minutos!
                                    </p>
                                </div>

                                <div className="bg-[#F5F3F0] p-4 md:p-6 rounded-xl">
                                    <h3 className="font-bold text-[#2C5F5D] mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                                        <MapPin size={18} className="text-[#D67441]" />
                                        Atendem em outras cidades?
                                    </h3>
                                    <p className="text-[#4A4A4A] text-xs md:text-sm">
                                        Sim! Atendemos todo o estado de São Paulo no varejo e região Sudeste no atacado. 
                                        Entre em contato para confirmar disponibilidade na sua região.
                                    </p>
                                </div>

                                <div className="bg-[#F5F3F0] p-4 md:p-6 rounded-xl">
                                    <h3 className="font-bold text-[#2C5F5D] mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                                        <Wrench size={18} className="text-[#D67441]" />
                                        Fazem visita técnica?
                                    </h3>
                                    <p className="text-[#4A4A4A] text-xs md:text-sm">
                                        Sim! Para projetos do COMBO, fazemos visita técnica gratuita para 
                                        medição, avaliação do ambiente e desenvolvimento do projeto personalizado.
                                    </p>
                                </div>
                            </Animated>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Final - MOBILE OTIMIZADO */}
            <section className="py-16 md:py-20 bg-gradient-to-br from-[#2C5F5D] to-[#1e4442] text-white">
                <div className="container mx-auto px-6 text-center">
                    <Animated>
                        <h2 className="text-2xl md:text-5xl font-bold font-title mb-4 md:mb-6">
                            Pronto para Começar 
                            <span className="block text-[#D67441]">Seu Projeto?</span>
                        </h2>
                    </Animated>
                    <Animated>
                        <p className="text-base md:text-xl text-gray-200 max-w-4xl mx-auto mb-8 md:mb-12 leading-relaxed">
                            A família Dsanzo está esperando sua ligação há mais de 40 anos. 
                            <strong className="text-white"> Vamos transformar seu ambiente juntos!</strong>
                        </p>
                    </Animated>
                    
                    <Animated className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center items-center">
                        <a 
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 md:gap-3 bg-[#25D366] text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-lg hover:bg-[#1da851] transition-all duration-300 transform hover:-translate-y-1 shadow-lg text-sm md:text-lg"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24z"/>
                            </svg>
                            Conversar no WhatsApp
                        </a>
                        <a 
                            href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                            className="flex items-center gap-2 md:gap-3 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-6 md:px-8 py-3 md:py-4 rounded-lg hover:border-[#D67441] text-sm md:text-lg"
                        >
                            <Phone size={20} />
                            Ligar: {CONTACT_INFO.phone}
                        </a>
                    </Animated>
                </div>
            </section>
        </div>
    );
};


// LEGAL PAGES
const LegalPage = ({ title, content, onNavigate }) => (
    <div className="animate-fade-in bg-white">
        <div className="bg-[#F5F3F0] py-24">
            <div className="container mx-auto px-6">
                <h1 className="font-title text-4xl md:text-5xl font-extrabold text-[#2C5F5D]">{title}</h1>
            </div>
        </div>
        <div className="py-20 container mx-auto px-6">
            <div className="prose lg:prose-xl max-w-4xl mx-auto">
                {content}
                <button onClick={() => onNavigate('Início')} className="mt-8 text-[#D67441] font-bold">Voltar ao Início</button>
            </div>
        </div>
    </div>
);

// CORPORATE PAGE - B2B Professional Landing Page
const CorporatePage = () => {
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [lightboxImage, setLightboxImage] = useState(null);

    // Imagens reais da pasta public/fotos com títulos
    const galleryImages = [
        { src: '/fotos/obra-escritorio-de-coworking-em-campinas.jpeg', title: 'Coworking em Campinas', tag: 'Corporativo', desc: 'Piso laminado espinha de peixe' },
        { src: '/fotos/hospital.jpeg', title: 'Ambiente Hospitalar', tag: 'Saúde', desc: 'Piso vinílico hospitalar' },
        { src: '/fotos/revestimento-feito.jpeg', title: 'Espaço Corporativo', tag: 'Corporativo', desc: 'Carpete modular' },
        { src: '/fotos/equipe-trabalhando1.jpeg', title: 'Preparação de Base', tag: 'Processo', desc: 'Nivelamento e preparação' },
        { src: '/fotos/equipe-trabalhando2.jpeg', title: 'Equipes Próprias', tag: 'Processo', desc: 'Instalação profissional' },
        { src: '/fotos/revestimento-pronto.jpeg', title: 'Ambiente Gastronômico', tag: 'Comercial', desc: 'Piso vinílico de alto tráfego' },
    ];

    // Atalhos para uso direto
    const realImages = {
        equipeTrabalhando1: '/fotos/equipe-trabalhando1.jpeg',
        equipeTrabalhando2: '/fotos/equipe-trabalhando2.jpeg',
        hospital: '/fotos/hospital.jpeg',
        coworkingCampinas: '/fotos/obra-escritorio-de-coworking-em-campinas.jpeg',
        obraEscritorio: '/fotos/obra-escritorio.jpeg',
        obraEscritorio2: '/fotos/obra-escritorio2.jpeg',
        revestimentoFeito: '/fotos/revestimento-feito.jpeg',
        revestimentoPronto: '/fotos/revestimento-pronto.jpeg',
    };

    const corporateSegments = [
        {
            id: 'corporativo',
            title: 'Corporativo',
            icon: <Building size={36} />,
            description: 'Escritórios, sedes empresariais e ambientes de trabalho que refletem a identidade da sua empresa.',
        },
        {
            id: 'comercial',
            title: 'Comercial',
            icon: <ShoppingBag size={36} />,
            description: 'Lojas, shoppings e espaços comerciais com alto tráfego e necessidade de durabilidade.',
        },
        {
            id: 'educacional',
            title: 'Educacional',
            icon: <Lightbulb size={36} />,
            description: 'Escolas, universidades e centros de treinamento com foco em segurança e conforto.',
        },
        {
            id: 'hoteleiro',
            title: 'Hoteleiro',
            icon: <Star size={36} />,
            description: 'Hotéis, resorts e pousadas que exigem elegância, conforto acústico e durabilidade.',
        },
        {
            id: 'esportivo',
            title: 'Esportivo',
            icon: <Award size={36} />,
            description: 'Academias, quadras e centros esportivos com pisos de alta performance e segurança.',
        },
        {
            id: 'saude',
            title: 'Saúde',
            icon: <ShieldCheck size={36} />,
            description: 'Hospitais, clínicas e laboratórios com conformidade sanitária e resistência química.',
        }
    ];

    const corporateStats = [
        { number: '1.000+', label: 'm² de Estoque Próprio', icon: <Layers size={28} /> },
        { number: '10', label: 'Anos de Garantia Produtos', icon: <ShieldCheck size={28} /> },
        { number: '40+', label: 'Anos de Experiência', icon: <Award size={28} /> },
        { number: '100%', label: 'Equipes Próprias', icon: <Users size={28} /> }
    ];

    const corporateDifferentials = [
        {
            icon: <FileText size={32} />,
            title: 'Análises Técnicas Completas',
            description: 'Realizadas nas fases de pré-obra, durante a execução e no pós-obra, garantindo compatibilidade entre base, materiais, método executivo e uso final.'
        },
        {
            icon: <ShieldCheck size={32} />,
            title: 'Conformidade Legal e Normativa',
            description: 'Equipes cobertas pelas Normas Regulamentadoras (NRs) e serviços formalizados por meio das ARTs necessárias, assegurando responsabilidade técnica e respaldo jurídico.'
        },
        {
            icon: <Layers size={32} />,
            title: 'Estoque Próprio Superior a 1.000m²',
            description: 'Todo o ecossistema de revestimentos: materiais de preparação de base, produtos de instalação, acabamentos, insumos de manutenção, limpeza e finalização estética.'
        },
        {
            icon: <TrendingUp size={32} />,
            title: 'Cadeia de Fornecimento Homologada',
            description: 'Complementa o estoque próprio, garantindo continuidade da obra, cumprimento de prazos e eliminação de paradas operacionais por indisponibilidade.'
        },
        {
            icon: <Users size={32} />,
            title: 'Equipes Próprias e Registradas',
            description: 'Instaladores treinados, registrados e permanentemente supervisionados pela DSANZO, eliminando terceirizações informais e riscos operacionais.'
        },
        {
            icon: <Ruler size={32} />,
            title: 'Relatório Executivo Completo',
            description: 'Documentação de todas as etapas com rastreabilidade técnica dos procedimentos, materiais aplicados e critérios de execução.'
        }
    ];

    return (
        <div className="animate-fade-in bg-white">
            {/* Hero Section Corporativo */}
            <section className="relative bg-gradient-to-br from-[#1a3d3b] via-[#2C5F5D] to-[#0f2221] text-white overflow-hidden min-h-[90vh] flex items-center">
                <div className="absolute inset-0 bg-black/30"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1920&q=80')] bg-cover bg-center opacity-20"></div>
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#D67441]/20 to-transparent"></div>
                
                <div className="relative container mx-auto px-6 py-20">
                    <div className="max-w-4xl">
                        <Animated>
                            <span className="inline-flex items-center gap-2 bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6 border border-[#D67441]/30">
                                <Building size={16} />
                                Pisos e Revestimentos Corporativos
                            </span>
                        </Animated>
                        
                        <Animated>
                            <h1 className="font-title text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                                DSANZO Pisos e
                                <span className="text-[#D67441] block">Revestimentos Corporativos</span>
                            </h1>
                        </Animated>
                        
                        <Animated>
                            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
                                Soluções completas em pisos e revestimentos <strong className="text-white">laminados e vinílicos</strong> para 
                                ambientes corporativos e residenciais de alto padrão.
                            </p>
                        </Animated>

                        <Animated>
                            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl border-l-4 border-[#D67441] pl-6">
                                Oferecemos a arquitetos, engenheiros, construtores e construtoras uma estrutura completa, 
                                capaz de assegurar <strong className="text-white">controle técnico, previsibilidade de execução 
                                e responsabilidade contratual</strong> em todas as etapas do projeto.
                            </p>
                        </Animated>

                        <Animated className="flex flex-col sm:flex-row gap-4 mb-12">
                            <CTAButton icon={ArrowRight} className="text-lg px-8 py-4 bg-[#D67441] hover:bg-[#B85A35]">
                                Solicitar Proposta Técnica
                            </CTAButton>
                            <a 
                                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                                className="flex items-center justify-center gap-2 text-white hover:text-[#D67441] transition-colors font-semibold border border-white/30 px-6 py-3 rounded-lg hover:border-[#D67441]"
                            >
                                <Phone size={20} />
                                Falar com Especialista
                            </a>
                        </Animated>

                        {/* Trust Badges Corporativos */}
                        <Animated className="flex flex-wrap items-center gap-4 text-sm">
                            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                                <ShieldCheck size={18} className="text-[#D67441]" />
                                <span>ARTs Emitidas</span>
                            </div>
                            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                                <FileText size={18} className="text-[#D67441]" />
                                <span>Conformidade NRs</span>
                            </div>
                            <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                                <Users size={18} className="text-[#D67441]" />
                                <span>Equipes Próprias</span>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* Proposta de Valor Única */}
            <section className="py-16 bg-[#2C5F5D] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="max-w-5xl mx-auto text-center">
                        <h2 className="text-2xl md:text-3xl font-bold font-title mb-6">
                            Responsabilidade Técnica Integral pelo Sistema de Revestimentos
                        </h2>
                        <p className="text-lg text-gray-200 leading-relaxed">
                            Diferentemente do modelo tradicional de mercado, a DSANZO atua como <strong className="text-[#D67441]">responsável 
                            técnica integral pelo sistema de revestimentos</strong>, assumindo de forma estruturada e documentada todo o processo — 
                            da análise técnica inicial à garantia pós-obra.
                        </p>
                    </Animated>
                </div>
            </section>

            {/* Estatísticas Corporativas */}
            <section className="py-16 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        {corporateStats.map((stat, index) => (
                            <Animated key={index} className="text-center">
                                <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 h-full">
                                    <div className="text-[#D67441] mb-3 flex justify-center">
                                        {stat.icon}
                                    </div>
                                    <div className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-1">
                                        {stat.number}
                                    </div>
                                    <p className="text-sm text-[#4A4A4A] font-medium">
                                        {stat.label}
                                    </p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* Galeria de Projetos */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4">
                            Nossos Projetos
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-6">
                            Excelência em Cada Detalhe
                        </h2>
                        <p className="text-xl text-[#4A4A4A] max-w-4xl mx-auto">
                            Da preparação da base ao acabamento final, cada projeto é executado com controle técnico 
                            rigoroso e rastreabilidade completa.
                        </p>
                    </Animated>

                    {/* Grid de Fotos - Layout Alinhado */}
                    {/* Mobile: 2 colunas empilhadas | Desktop: Coworking à esquerda, 4 fotos + restaurante à direita */}
                    <div className="flex flex-col lg:flex-row gap-3">
                        {/* Coluna Esquerda - Coworking */}
                        <Animated className="lg:w-1/3">
                            <div 
                                className="relative h-72 lg:h-[648px] rounded-2xl overflow-hidden group cursor-pointer"
                                onClick={() => setLightboxImage(galleryImages[0])}
                            >
                                <img 
                                    src={realImages.coworkingCampinas} 
                                    alt="Escritório de Coworking em Campinas" 
                                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent group-hover:from-black/80 transition-all"></div>
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="bg-white/20 backdrop-blur-sm p-2 rounded-full">
                                        <Maximize2 size={20} className="text-white" />
                                    </div>
                                </div>
                                <div className="absolute bottom-4 left-4 right-4">
                                    <span className="inline-block bg-[#D67441] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                                        Corporativo
                                    </span>
                                    <h3 className="text-xl lg:text-2xl font-bold text-white font-title">
                                        Coworking em Campinas
                                    </h3>
                                    <p className="text-gray-200 text-sm mt-1">Piso laminado espinha de peixe</p>
                                </div>
                            </div>
                        </Animated>

                        {/* Coluna Direita - Grid de fotos */}
                        <div className="lg:w-2/3 flex flex-col gap-3">
                            {/* Linha 1 - Hospital e Corporativo */}
                            <div className="grid grid-cols-2 gap-3">
                                <Animated>
                                    <div 
                                        className="relative h-40 lg:h-52 rounded-2xl overflow-hidden group cursor-pointer"
                                        onClick={() => setLightboxImage(galleryImages[1])}
                                    >
                                        <img 
                                            src={realImages.hospital} 
                                            alt="Projeto Hospitalar" 
                                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-all"></div>
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
                                                <Maximize2 size={16} className="text-white" />
                                            </div>
                                        </div>
                                        <div className="absolute bottom-3 left-3 right-3">
                                            <span className="inline-block bg-[#D67441] text-white text-xs font-bold px-2 py-1 rounded-full mb-1">
                                                Saúde
                                            </span>
                                            <h3 className="text-sm font-bold text-white font-title">
                                                Ambiente Hospitalar
                                            </h3>
                                        </div>
                                    </div>
                                </Animated>

                                <Animated>
                                    <div 
                                        className="relative h-40 lg:h-52 rounded-2xl overflow-hidden group cursor-pointer"
                                        onClick={() => setLightboxImage(galleryImages[2])}
                                    >
                                        <img 
                                            src={realImages.revestimentoFeito} 
                                            alt="Ambiente Corporativo" 
                                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-all"></div>
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
                                                <Maximize2 size={16} className="text-white" />
                                            </div>
                                        </div>
                                        <div className="absolute bottom-3 left-3 right-3">
                                            <span className="inline-block bg-[#D67441] text-white text-xs font-bold px-2 py-1 rounded-full mb-1">
                                                Corporativo
                                            </span>
                                            <h3 className="text-sm font-bold text-white font-title">
                                                Espaço Corporativo
                                            </h3>
                                        </div>
                                    </div>
                                </Animated>
                            </div>

                            {/* Linha 2 - Equipes */}
                            <div className="grid grid-cols-2 gap-3">
                                <Animated>
                                    <div 
                                        className="relative h-40 lg:h-52 rounded-2xl overflow-hidden group cursor-pointer"
                                        onClick={() => setLightboxImage(galleryImages[3])}
                                    >
                                        <img 
                                            src={realImages.equipeTrabalhando1} 
                                            alt="Equipe DSANZO trabalhando" 
                                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-all"></div>
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
                                                <Maximize2 size={16} className="text-white" />
                                            </div>
                                        </div>
                                        <div className="absolute bottom-3 left-3 right-3">
                                            <h3 className="text-sm font-bold text-white font-title">
                                                Preparação de Base
                                            </h3>
                                        </div>
                                    </div>
                                </Animated>

                                <Animated>
                                    <div 
                                        className="relative h-40 lg:h-52 rounded-2xl overflow-hidden group cursor-pointer"
                                        onClick={() => setLightboxImage(galleryImages[4])}
                                    >
                                        <img 
                                            src={realImages.equipeTrabalhando2} 
                                            alt="Equipe DSANZO em obra" 
                                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent group-hover:from-black/80 transition-all"></div>
                                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
                                                <Maximize2 size={16} className="text-white" />
                                            </div>
                                        </div>
                                        <div className="absolute bottom-3 left-3 right-3">
                                            <h3 className="text-sm font-bold text-white font-title">
                                                Equipes Próprias
                                            </h3>
                                        </div>
                                    </div>
                                </Animated>
                            </div>

                            {/* Linha 3 - Restaurante (toda a largura da coluna direita) */}
                            <Animated>
                                <div 
                                    className="relative h-40 lg:h-52 rounded-2xl overflow-hidden group cursor-pointer"
                                    onClick={() => setLightboxImage(galleryImages[5])}
                                >
                                    <img 
                                        src={realImages.revestimentoPronto} 
                                        alt="Restaurante com piso vinílico" 
                                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent group-hover:from-black/80 transition-all"></div>
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="bg-white/20 backdrop-blur-sm p-1.5 rounded-full">
                                            <Maximize2 size={16} className="text-white" />
                                        </div>
                                    </div>
                                    <div className="absolute bottom-3 left-3 right-3">
                                        <span className="inline-block bg-[#D67441] text-white text-xs font-bold px-2 py-1 rounded-full mb-1">
                                            Comercial
                                        </span>
                                        <h3 className="text-lg font-bold text-white font-title">
                                            Ambiente Gastronômico
                                        </h3>
                                        <p className="text-gray-200 text-xs">Piso vinílico de alto tráfego</p>
                                    </div>
                                </div>
                            </Animated>
                        </div>
                    </div>
                </div>
            </section>

            {/* Segmentos Atendidos */}
            <section className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-4">
                            Segmentos Atendidos
                        </span>
                        <h2 className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-6">
                            Soluções para Cada Necessidade
                        </h2>
                        <p className="text-xl text-[#4A4A4A] max-w-4xl mx-auto">
                            Nossos materiais e serviços atendem às demandas específicas de cada segmento, 
                            com conhecimento técnico especializado e produtos adequados.
                        </p>
                    </Animated>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {corporateSegments.map((segment) => (
                            <Animated key={segment.id}>
                                <div 
                                    className={cl(
                                        "bg-white p-6 rounded-2xl cursor-pointer transition-all duration-300 text-center h-full border-2",
                                        selectedSegment === segment.id 
                                            ? "border-[#D67441] shadow-xl" 
                                            : "border-transparent hover:border-[#2C5F5D]/30 hover:shadow-lg"
                                    )}
                                    onClick={() => setSelectedSegment(selectedSegment === segment.id ? null : segment.id)}
                                >
                                    <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-[#2C5F5D] to-[#1a3d3b] rounded-xl flex items-center justify-center text-white shadow-lg">
                                        {segment.icon}
                                    </div>
                                    <h3 className="text-base font-bold text-[#2C5F5D] font-title">
                                        {segment.title}
                                    </h3>
                                </div>
                            </Animated>
                        ))}
                    </div>

                    {selectedSegment && (
                        <Animated className="mt-8">
                            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-2xl mx-auto text-center">
                                <p className="text-lg text-[#4A4A4A] leading-relaxed">
                                    {corporateSegments.find(s => s.id === selectedSegment)?.description}
                                </p>
                            </div>
                        </Animated>
                    )}
                </div>
            </section>

            {/* Diferenciais Técnicos */}
            <section className="py-20 bg-gradient-to-br from-[#2C5F5D] to-[#1a3d3b] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold font-title mb-6">
                            O Que Entregamos
                        </h2>
                        <p className="text-xl text-gray-200 max-w-4xl mx-auto">
                            Um sistema completo de garantias técnicas, jurídicas e financeiras que assegura 
                            previsibilidade, controle e segurança em todas as etapas.
                        </p>
                    </Animated>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {corporateDifferentials.map((diff, index) => (
                            <Animated key={index}>
                                <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 h-full">
                                    <div className="text-[#D67441] mb-6">
                                        {diff.icon}
                                    </div>
                                    <h3 className="text-xl font-bold font-title mb-4">
                                        {diff.title}
                                    </h3>
                                    <p className="text-gray-200 leading-relaxed text-sm">
                                        {diff.description}
                                    </p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* Garantias */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <Animated>
                            <div className="grid grid-cols-2 gap-4">
                                <img 
                                    src={realImages.obraEscritorio} 
                                    alt="Obra em andamento" 
                                    className="rounded-2xl shadow-lg h-64 object-cover w-full"
                                />
                                <img 
                                    src={realImages.obraEscritorio2} 
                                    alt="Obra em execução" 
                                    className="rounded-2xl shadow-lg h-64 object-cover w-full mt-8"
                                />
                            </div>
                        </Animated>
                        
                        <Animated>
                            <span className="inline-block bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6">
                                Nossas Garantias
                            </span>
                            <h2 className="text-3xl md:text-4xl font-bold text-[#2C5F5D] font-title mb-6">
                                Responsabilização Integral
                            </h2>
                            <p className="text-lg text-[#4A4A4A] mb-8 leading-relaxed">
                                Assumimos responsabilidade <strong className="text-[#2C5F5D]">técnica, jurídica e financeira</strong> pelo 
                                escopo contratado, durante a execução e no período pós-obra, conforme os termos de garantia estabelecidos.
                            </p>
                            
                            <div className="space-y-6">
                                <div className="flex items-start gap-4 bg-[#F5F3F0] p-6 rounded-xl">
                                    <div className="w-12 h-12 bg-[#D67441] rounded-xl flex items-center justify-center flex-shrink-0">
                                        <ShieldCheck size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-[#2C5F5D] text-lg">Garantia de Produtos</h4>
                                        <p className="text-[#4A4A4A]">A partir de <strong>10 anos</strong> nos produtos residenciais</p>
                                    </div>
                                </div>
                                
                                <div className="flex items-start gap-4 bg-[#F5F3F0] p-6 rounded-xl">
                                    <div className="w-12 h-12 bg-[#2C5F5D] rounded-xl flex items-center justify-center flex-shrink-0">
                                        <Wrench size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-[#2C5F5D] text-lg">Garantia de Mão de Obra</h4>
                                        <p className="text-[#4A4A4A]">Garantia total a partir de <strong>1 ano</strong>, com rastreabilidade técnica</p>
                                    </div>
                                </div>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* Processo */}
            <section className="py-20 bg-[#F5F3F0]">
                <div className="container mx-auto px-6">
                    <Animated className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold text-[#2C5F5D] font-title mb-6">
                            Nosso Processo Estruturado
                        </h2>
                        <p className="text-xl text-[#4A4A4A] max-w-4xl mx-auto">
                            Metodologia que garante controle técnico, previsibilidade de execução e documentação completa.
                        </p>
                    </Animated>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { step: '01', title: 'Análise Técnica Pré-Obra', desc: 'Avaliação de compatibilidade entre base, materiais, método executivo e uso final', icon: <FileText size={24} /> },
                            { step: '02', title: 'Planejamento e Fornecimento', desc: 'Estoque próprio e cadeia homologada garantindo continuidade e prazos', icon: <Layers size={24} /> },
                            { step: '03', title: 'Execução Supervisionada', desc: 'Equipes próprias, treinadas e registradas com conformidade NRs', icon: <Users size={24} /> },
                            { step: '04', title: 'Entrega e Documentação', desc: 'Relatório executivo com rastreabilidade técnica e garantias', icon: <ShieldCheck size={24} /> }
                        ].map((item, index) => (
                            <Animated key={index}>
                                <div className="relative bg-white p-6 rounded-2xl text-center h-full shadow-lg">
                                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-10 h-10 bg-[#D67441] rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                                        {item.step}
                                    </div>
                                    <div className="mt-6 mb-4 text-[#2C5F5D] flex justify-center">
                                        {item.icon}
                                    </div>
                                    <h3 className="font-bold text-[#2C5F5D] font-title mb-2">{item.title}</h3>
                                    <p className="text-sm text-[#4A4A4A] leading-relaxed">{item.desc}</p>
                                </div>
                            </Animated>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mensagem Final + CTA */}
            <section className="py-20 bg-[#2C5F5D] text-white">
                <div className="container mx-auto px-6">
                    <Animated className="max-w-5xl mx-auto text-center mb-12">
                        <h2 className="text-2xl md:text-3xl font-bold font-title mb-6 leading-relaxed">
                            Na DSANZO Pisos Corporativos, não comercializamos apenas o revestimento necessário para a obra.
                        </h2>
                        <p className="text-xl text-gray-200 leading-relaxed">
                            Entregamos um <strong className="text-[#D67441]">sistema completo de garantias técnicas, jurídicas e financeiras</strong>, 
                            que assegura previsibilidade, controle e segurança qualitativa, operacional e executiva — tanto para 
                            quem projeta e constrói quanto para o cliente final que reconhece o valor estratégico da tranquilidade 
                            proporcionada por um processo plenamente profissional, assumido e controlado.
                        </p>
                    </Animated>
                </div>
            </section>

            {/* CTA Corporativo Final */}
            <section className="py-20 bg-gradient-to-br from-[#1a3d3b] via-[#2C5F5D] to-[#0f2221] text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                
                <div className="relative container mx-auto px-6">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <Animated>
                            <span className="inline-flex items-center gap-2 bg-[#D67441]/20 text-[#D67441] font-bold px-4 py-2 rounded-full text-sm uppercase tracking-wider mb-6 border border-[#D67441]/30">
                                <Briefcase size={16} />
                                Contato Comercial
                            </span>
                            <h2 className="text-3xl md:text-5xl font-bold font-title mb-6">
                                Vamos Estruturar
                                <span className="block text-[#D67441]">Seu Projeto?</span>
                            </h2>
                            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                                Nossa equipe técnica está pronta para entender suas necessidades e apresentar 
                                uma proposta com especificações completas, prazos e garantias.
                            </p>
                            
                            <div className="space-y-4 mb-8">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-[#D67441] rounded-xl flex items-center justify-center">
                                        <Phone size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-300">Telefone Comercial</p>
                                        <p className="font-bold text-xl">{CONTACT_INFO.phone}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-[#D67441] rounded-xl flex items-center justify-center">
                                        <Mail size={24} className="text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-300">E-mail Corporativo</p>
                                        <p className="font-bold text-xl">corporativo@grupodsanzo.com.br</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-4">
                                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                                    <ShieldCheck size={16} className="text-[#D67441]" />
                                    Proposta técnica detalhada
                                </div>
                                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm">
                                    <FileText size={16} className="text-[#D67441]" />
                                    ART incluída
                                </div>
                            </div>
                        </Animated>
                        
                        <Animated className="relative">
                            <div className="bg-white p-8 rounded-2xl shadow-2xl text-center">
                                <h3 className="text-2xl font-bold text-[#2C5F5D] font-title mb-2 text-center">
                                    Solicite uma Proposta Técnica
                                </h3>
                                <p className="text-sm text-[#4A4A4A] mb-6">
                                    Para proposta técnica detalhada ou ART, utilize o formulário de orçamento acima. Indique no campo &quot;Conte sobre seu projeto&quot; que deseja uma proposta técnica e nossa equipe entrará em contato em até 24h úteis.
                                </p>
                                <a href="#form-corporativo" className="inline-flex items-center justify-center gap-2 group font-bold py-3 px-6 rounded-lg bg-[#D67441] text-white hover:bg-opacity-90 shadow-lg hover:shadow-xl transition-all duration-300">
                                    Ir ao formulário <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                </a>
                                <p className="text-xs text-gray-500 text-center mt-4">
                                    Seus dados estão seguros • Resposta em até 24h úteis
                                </p>
                            </div>
                        </Animated>
                    </div>
                </div>
            </section>

            {/* Lightbox Modal */}
            {lightboxImage && (
                <div 
                    className="fixed inset-0 bg-black/90 z-[999] flex items-center justify-center p-4 animate-fade-in"
                    onClick={() => setLightboxImage(null)}
                >
                    <button 
                        onClick={() => setLightboxImage(null)} 
                        className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all z-20"
                    >
                        <X size={28} />
                    </button>
                    
                    <div 
                        className="relative max-w-5xl max-h-[90vh] w-full"
                        onClick={e => e.stopPropagation()}
                    >
                        <img 
                            src={lightboxImage.src} 
                            alt={lightboxImage.title}
                            className="w-full h-auto max-h-[80vh] object-contain rounded-lg shadow-2xl"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                            <span className="inline-block bg-[#D67441] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                                {lightboxImage.tag}
                            </span>
                            <h3 className="text-2xl font-bold text-white font-title">
                                {lightboxImage.title}
                            </h3>
                            <p className="text-gray-300 text-sm mt-1">{lightboxImage.desc}</p>
                        </div>
                    </div>

                    {/* Navegação entre imagens */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            const currentIdx = galleryImages.findIndex(img => img.src === lightboxImage.src);
                            const prevIdx = (currentIdx - 1 + galleryImages.length) % galleryImages.length;
                            setLightboxImage(galleryImages[prevIdx]);
                        }}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all"
                    >
                        <ChevronLeft size={28} />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            const currentIdx = galleryImages.findIndex(img => img.src === lightboxImage.src);
                            const nextIdx = (currentIdx + 1) % galleryImages.length;
                            setLightboxImage(galleryImages[nextIdx]);
                        }}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-white/10 hover:bg-white/20 rounded-full p-3 transition-all"
                    >
                        <ChevronRight size={28} />
                    </button>

                    {/* Indicadores */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {galleryImages.map((img, idx) => (
                            <button
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setLightboxImage(img);
                                }}
                                className={cl(
                                    "w-2 h-2 rounded-full transition-all",
                                    lightboxImage.src === img.src ? "w-6 bg-[#D67441]" : "bg-white/50 hover:bg-white/80"
                                )}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// Main App Component
export default function App() {
    const [currentPage, setCurrentPage] = useState('Início');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);

    // Effect to handle body scroll based on menu state
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        // Cleanup function
        return () => {
            document.body.style.overflow = 'auto';
        }
    }, [isMenuOpen]);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    const handleNavigate = (page) => {
        setCurrentPage(page);
        setIsMenuOpen(false);
        window.scrollTo(0, 0);
    };

    const renderPage = () => {
        switch (currentPage) {
            case 'Sobre': return <AboutPage />;
            case 'Produtos': return <ProductsPage />;
            case 'Serviços': return <ServicesPage />;
            case 'Portfólio': return <PortfolioPage />;
            case 'Corporativo': return <CorporatePage />;
            case 'Contato': return <ContactPage />;
            
            case 'Política de Privacidade': return <LegalPage title="Política de Privacidade" onNavigate={handleNavigate} content={
    <>
        <p className="text-lg font-semibold text-[#2C5F5D] mb-4">Última atualização: 30 de junho de 2025</p>
        
        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">1. Informações Gerais</h3>
        <p>O Grupo Dsanzo, pessoa jurídica de direito privado, com sede na Av. Francisco de Paula Leite, 230 - Jardim Pedroso, Indaiatuba - SP, CEP 13343-040, inscrita no CNPJ sob o nº [inserir CNPJ], doravante denominada "Dsanzo", está comprometida com a proteção da privacidade e dos dados pessoais de seus clientes, visitantes do site e usuários de seus serviços.</p>
        
        <p>Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e protegemos suas informações pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018) e demais legislações aplicáveis.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">2. Informações que Coletamos</h3>
        <p><strong>2.1 Informações fornecidas voluntariamente:</strong></p>
        <ul className="list-disc pl-6 mb-4">
            <li>Nome completo, e-mail, telefone e endereço quando você solicita orçamentos</li>
            <li>Informações sobre seu projeto quando utiliza nossos serviços</li>
            <li>Dados de contato quando se inscreve em nossa newsletter ou participa de promoções</li>
            <li>Mensagens enviadas através de formulários de contato ou WhatsApp</li>
        </ul>

        <p><strong>2.2 Informações coletadas automaticamente:</strong></p>
        <ul className="list-disc pl-6 mb-4">
            <li>Endereço IP, tipo de navegador e sistema operacional</li>
            <li>Páginas visitadas, tempo de permanência e origem do acesso</li>
            <li>Cookies e tecnologias similares para melhorar sua experiência</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">3. Como Utilizamos suas Informações</h3>
        <p>Utilizamos suas informações pessoais para:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Fornecer orçamentos e informações sobre nossos produtos e serviços</li>
            <li>Processar e executar contratos de prestação de serviços</li>
            <li>Comunicar sobre o andamento de projetos e instalações</li>
            <li>Enviar newsletters e comunicações promocionais (com seu consentimento)</li>
            <li>Melhorar nossos produtos, serviços e experiência do usuário</li>
            <li>Cumprir obrigações legais e regulamentares</li>
            <li>Exercer direitos em processos judiciais, administrativos ou arbitrais</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">4. Base Legal para o Tratamento</h3>
        <p>O tratamento de seus dados pessoais é fundamentado nas seguintes bases legais:</p>
        <ul className="list-disc pl-6 mb-4">
            <li><strong>Consentimento:</strong> quando você autoriza expressamente o uso de seus dados</li>
            <li><strong>Execução de contrato:</strong> para cumprimento de obrigações contratuais</li>
            <li><strong>Legítimo interesse:</strong> para melhorias em nossos serviços e comunicação comercial</li>
            <li><strong>Cumprimento de obrigação legal:</strong> quando exigido por lei</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">5. Compartilhamento de Informações</h3>
        <p>Não vendemos, alugamos ou compartilhamos suas informações pessoais com terceiros, exceto nas seguintes situações:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Com prestadores de serviços que nos auxiliam em nossas operações (sempre sob contrato de confidencialidade)</li>
            <li>Com fornecedores de produtos quando necessário para execução de projetos</li>
            <li>Quando exigido por lei, ordem judicial ou autoridades competentes</li>
            <li>Para proteção de nossos direitos legais ou segurança</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">6. Segurança dos Dados</h3>
        <p>Implementamos medidas técnicas e organizacionais adequadas para proteger suas informações pessoais contra acesso não autorizado, alteração, divulgação ou destruição, incluindo:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Criptografia de dados sensíveis</li>
            <li>Controles de acesso rigorosos</li>
            <li>Treinamento regular de funcionários sobre proteção de dados</li>
            <li>Monitoramento contínuo de sistemas de segurança</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">7. Retenção de Dados</h3>
        <p>Mantemos suas informações pessoais apenas pelo tempo necessário para cumprir as finalidades descritas nesta política, respeitando prazos legais de retenção. Dados de clientes são mantidos durante a vigência do contrato e por até 5 anos após seu término para fins de garantia e suporte.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">8. Seus Direitos</h3>
        <p>De acordo com a LGPD, você tem os seguintes direitos:</p>
        <ul className="list-disc pl-6 mb-4">
            <li><strong>Acesso:</strong> solicitar informações sobre seus dados pessoais</li>
            <li><strong>Correção:</strong> solicitar correção de dados incompletos ou incorretos</li>
            <li><strong>Exclusão:</strong> solicitar eliminação de dados desnecessários ou tratados incorretamente</li>
            <li><strong>Portabilidade:</strong> solicitar transferência de dados para outro fornecedor</li>
            <li><strong>Oposição:</strong> opor-se ao tratamento de dados</li>
            <li><strong>Revogação do consentimento:</strong> retirar consentimento a qualquer momento</li>
        </ul>

        <p>Para exercer seus direitos, entre em contato conosco através do e-mail: privacidade@grupodsanzo.com.br ou telefone (19) 98914-4191.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">9. Cookies</h3>
        <p>Utilizamos cookies para melhorar sua experiência em nosso site. Você pode configurar seu navegador para recusar cookies, mas isso pode afetar algumas funcionalidades do site.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">10. Alterações nesta Política</h3>
        <p>Esta Política de Privacidade pode ser atualizada periodicamente. Notificaremos sobre mudanças significativas através de nosso site ou por e-mail.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">11. Contato</h3>
        <p>Para dúvidas sobre esta Política de Privacidade ou sobre o tratamento de seus dados pessoais:</p>
        <ul className="list-disc pl-6 mb-4">
            <li><strong>E-mail:</strong> privacidade@grupodsanzo.com.br</li>
            <li><strong>Telefone:</strong> (19) 98914-4191</li>
            <li><strong>Endereço:</strong> Av. Francisco de Paula Leite, 230 - Jardim Pedroso, Indaiatuba - SP, CEP 13343-040</li>
        </ul>
    </>
} />;

case 'Termos de Uso': return <LegalPage title="Termos de Uso" onNavigate={handleNavigate} content={
    <>
        <p className="text-lg font-semibold text-[#2C5F5D] mb-4">Última atualização: 30 de junho de 2025</p>
        
        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">1. Aceitação dos Termos</h3>
        <p>Ao acessar e utilizar o site do Grupo Dsanzo (www.grupodsanzo.com.br), você concorda em cumprir e estar vinculado a estes Termos de Uso. Se você não concordar com qualquer parte destes termos, não deve utilizar nosso site ou serviços.</p>

        <p>Estes termos constituem um acordo legal entre você e o Grupo Dsanzo, pessoa jurídica inscrita no CNPJ [inserir CNPJ], com sede na Av. Francisco de Paula Leite, 230 - Jardim Pedroso, Indaiatuba - SP.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">2. Descrição dos Serviços</h3>
        <p>O Grupo Dsanzo oferece:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Venda de pisos vinílicos, laminados, carpetes, forros e acessórios</li>
            <li>Serviços de instalação especializada (modelo COMBO)</li>
            <li>Consultoria e projetos personalizados</li>
            <li>Venda no atacado para profissionais do setor</li>
            <li>Treinamento para instaladores</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">3. Uso Permitido</h3>
        <p>Você pode utilizar nosso site para:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Navegar e visualizar informações sobre produtos e serviços</li>
            <li>Solicitar orçamentos e informações comerciais</li>
            <li>Entrar em contato conosco através dos canais disponibilizados</li>
            <li>Acessar conteúdo educativo sobre pisos e revestimentos</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">4. Uso Proibido</h3>
        <p>É expressamente proibido:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Utilizar o site para fins ilegais ou não autorizados</li>
            <li>Tentar obter acesso não autorizado a sistemas ou dados</li>
            <li>Reproduzir, distribuir ou modificar conteúdo sem autorização</li>
            <li>Enviar spam, vírus ou códigos maliciosos</li>
            <li>Interferir no funcionamento normal do site</li>
            <li>Utilizar informações para concorrência desleal</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">5. Propriedade Intelectual</h3>
        <p>Todo o conteúdo do site, incluindo textos, imagens, logotipos, design, código-fonte e funcionalidades, é de propriedade exclusiva do Grupo Dsanzo ou de seus licenciadores, sendo protegido por leis de direitos autorais e propriedade intelectual.</p>

        <p>É permitido visualizar e imprimir páginas do site apenas para uso pessoal e não comercial, mantendo todos os avisos de direitos autorais.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">6. Orçamentos e Contratação</h3>
        <p><strong>6.1</strong> Os orçamentos fornecidos através do site são estimativas baseadas nas informações fornecidas e têm validade de 30 dias.</p>
        
        <p><strong>6.2</strong> A contratação de serviços será formalizada através de contrato específico, que prevalecerá sobre estes termos gerais em caso de conflito.</p>
        
        <p><strong>6.3</strong> Preços estão sujeitos a alteração sem aviso prévio. O preço final será confirmado no momento da contratação.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">7. Garantias e Responsabilidades</h3>
        <p><strong>7.1</strong> Nossos produtos possuem garantia conforme especificações dos fabricantes e legislação aplicável.</p>
        
        <p><strong>7.2</strong> Serviços de instalação possuem garantia própria conforme contrato específico.</p>
        
        <p><strong>7.3</strong> Não nos responsabilizamos por danos decorrentes de uso inadequado dos produtos ou não observância das recomendações técnicas.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">8. Limitação de Responsabilidade</h3>
        <p>O Grupo Dsanzo não será responsável por:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Interrupções ou falhas no funcionamento do site</li>
            <li>Perda de dados ou informações</li>
            <li>Danos indiretos, incidentais ou consequenciais</li>
            <li>Ações de terceiros ou links externos</li>
            <li>Decisões tomadas com base em informações do site</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">9. Links para Sites de Terceiros</h3>
        <p>Nosso site pode conter links para sites de terceiros. Não somos responsáveis pelo conteúdo, políticas de privacidade ou práticas desses sites. O acesso é por sua conta e risco.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">10. Modificações</h3>
        <p>Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. As alterações entrarão em vigor imediatamente após sua publicação no site. O uso continuado do site após as modificações constitui aceitação dos novos termos.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">11. Rescisão</h3>
        <p>Podemos suspender ou encerrar seu acesso ao site a qualquer momento, sem aviso prévio, por violação destes termos ou por qualquer outro motivo que consideremos apropriado.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">12. Lei Aplicável e Foro</h3>
        <p>Estes Termos de Uso são regidos pelas leis brasileiras. Fica eleito o foro da comarca de Indaiatuba/SP para dirimir quaisquer controvérsias decorrentes deste acordo.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">13. Contato</h3>
        <p>Para dúvidas sobre estes Termos de Uso:</p>
        <ul className="list-disc pl-6 mb-4">
            <li><strong>E-mail:</strong> contato@grupodsanzo.com.br</li>
            <li><strong>Telefone:</strong> (19) 98914-4191</li>
            <li><strong>Endereço:</strong> Av. Francisco de Paula Leite, 230 - Jardim Pedroso, Indaiatuba - SP, CEP 13343-040</li>
        </ul>
    </>
} />;

case 'Aviso Legal': return <LegalPage title="Aviso Legal" onNavigate={handleNavigate} content={
    <>
        <p className="text-lg font-semibold text-[#2C5F5D] mb-4">Última atualização: 30 de junho de 2025</p>
        
        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">1. Informações da Empresa</h3>
        <p><strong>Razão Social:</strong> [Inserir Razão Social Completa]<br/>
        <strong>Nome Fantasia:</strong> Grupo Dsanzo<br/>
        <strong>CNPJ:</strong> [Inserir CNPJ]<br/>
        <strong>Endereço:</strong> Av. Francisco de Paula Leite, 230 - Jardim Pedroso, Indaiatuba - SP, CEP 13343-040<br/>
        <strong>Telefone:</strong> (19) 98914-4191<br/>
        <strong>E-mail:</strong> contato@grupodsanzo.com.br</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">2. Natureza das Informações</h3>
        <p>As informações contidas neste site são fornecidas pelo Grupo Dsanzo exclusivamente para fins informativos e comerciais. Embora nos esforcemos para manter todas as informações atualizadas, precisas e completas, não garantimos a exatidão, completude ou atualidade de todo o conteúdo apresentado.</p>

        <p>As especificações técnicas, preços, disponibilidade de produtos e demais informações comerciais estão sujeitas a alterações sem aviso prévio.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">3. Uso das Informações</h3>
        <p>As informações deste site não devem ser consideradas como aconselhamento profissional específico. Para decisões importantes relacionadas a projetos de construção, reformas ou instalações, recomendamos sempre consultar nossos especialistas ou profissionais qualificados.</p>

        <p>O usuário é responsável por verificar a adequação das informações às suas necessidades específicas antes de tomar qualquer decisão baseada no conteúdo deste site.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">4. Limitações e Exclusões de Responsabilidade</h3>
        <p>O Grupo Dsanzo não se responsabiliza por:</p>
        <ul className="list-disc pl-6 mb-4">
            <li>Erros, omissões ou imprecisões nas informações fornecidas</li>
            <li>Indisponibilidade temporária ou permanente do site</li>
            <li>Perda de dados ou interrupção de serviços</li>
            <li>Decisões tomadas com base nas informações do site</li>
            <li>Danos diretos, indiretos, incidentais ou consequenciais</li>
            <li>Ações de terceiros ou conteúdo de sites externos</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">5. Produtos e Serviços</h3>
        <p><strong>5.1 Disponibilidade:</strong> A disponibilidade de produtos está sujeita ao estoque e às condições de fornecimento de nossos parceiros fabricantes.</p>
        
        <p><strong>5.2 Preços:</strong> Todos os preços apresentados são sugestivos e podem variar conforme região, quantidade, condições de pagamento e outros fatores. Preços finais serão confirmados no momento da contratação.</p>
        
        <p><strong>5.3 Especificações:</strong> As especificações técnicas dos produtos podem sofrer alterações pelos fabricantes sem aviso prévio. Sempre consulte as especificações mais atuais antes da compra.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">6. Imagens e Representações Visuais</h3>
        <p>As imagens de produtos e ambientes apresentadas no site têm caráter meramente ilustrativo. Cores, texturas e acabamentos podem variar conforme configurações de monitor, iluminação do ambiente e lote de fabricação dos produtos.</p>

        <p>Recomendamos sempre solicitar amostras físicas dos produtos antes da decisão final de compra.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">7. Marcas e Direitos Autorais</h3>
        <p>Todas as marcas, logotipos, nomes comerciais e direitos autorais mencionados neste site pertencem aos seus respectivos proprietários. O uso de marcas de terceiros destina-se apenas à identificação dos produtos que comercializamos e não implica em qualquer associação, patrocínio ou endosso.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">8. Área de Cobertura</h3>
        <p>Nossos serviços de instalação e entrega são oferecidos prioritariamente na região de Campinas, Indaiatuba e cidades próximas. Para outras localidades, consulte disponibilidade e condições especiais.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">9. Relacionamento com Fornecedores</h3>
        <p>O Grupo Dsanzo atua como distribuidor autorizado de diversas marcas reconhecidas no mercado. Mantemos parcerias comerciais com fabricantes, mas não somos responsáveis por políticas, decisões ou ações desses fornecedores que estejam além de nosso controle.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">10. Comunicações Eletrônicas</h3>
        <p>Ao fornecer seu endereço de e-mail ou número de telefone, você consente em receber comunicações eletrônicas relacionadas aos nossos produtos, serviços e promoções. Você pode cancelar essas comunicações a qualquer momento através dos links de descadastro ou entrando em contato conosco.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">11. Atualizações deste Aviso</h3>
        <p>Este Aviso Legal pode ser atualizado periodicamente para refletir mudanças em nossas práticas comerciais ou exigências legais. A versão mais atual estará sempre disponível em nosso site.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">12. Legislação Aplicável</h3>
        <p>Este aviso legal é regido pelas leis brasileiras, incluindo o Código de Defesa do Consumidor, Marco Civil da Internet e Lei Geral de Proteção de Dados.</p>

        <h3 className="text-2xl font-bold text-[#2C5F5D] mt-8 mb-4">13. Contato para Questões Legais</h3>
        <p>Para questões relacionadas a este Aviso Legal ou outros assuntos jurídicos:</p>
        <ul className="list-disc pl-6 mb-4">
            <li><strong>E-mail:</strong> juridico@grupodsanzo.com.br</li>
            <li><strong>Telefone:</strong> (19) 98914-4191</li>
            <li><strong>Endereço:</strong> Av. Francisco de Paula Leite, 230 - Jardim Pedroso, Indaiatuba - SP, CEP 13343-040</li>
        </ul>

        <p className="mt-8 p-4 bg-[#F5F3F0] rounded-lg border-l-4 border-[#D67441]">
            <strong>Importante:</strong> Este aviso legal não substitui os termos específicos de contratos de compra e venda ou prestação de serviços, que prevalecerão em caso de conflito.
        </p>
    </>
} />;
            case 'Início': default: return <HomePage onNavigate={handleNavigate} />;
        }
    };

    return (
    <Router>
        <Routes>
            {/* Rota específica para Admin */}
            <Route path="/admin" element={<Admin />} />
            
            {/* Todas as outras rotas mantêm seu site atual */}
            <Route path="/*" element={
                <div className="bg-white" style={{ fontFamily: "'Open Sans', sans-serif" }}>
                    <style>
                        {`
                          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Open+Sans:wght@400;600&display=swap');
                          .font-title { font-family: 'Poppins', sans-serif; }
                          .animate-fade-in { animation: fadeIn 0.5s ease-in-out; }
                          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                          
                          .animate-fade-in-up {
                              opacity: 0;
                              animation: fadeInUp 0.8s ease-out forwards;
                          }
                          @keyframes fadeInUp {
                              from { opacity: 0; transform: translateY(20px); }
                              to { opacity: 1; transform: translateY(0); }
                          }

                          .animate-slide-in {
                              animation: slideIn 0.5s ease-out forwards;
                          }
                           @keyframes slideIn {
                              from { transform: translateX(100%); }
                              to { transform: translateX(0); }
                          }

                          .menu-item-animate {
                            opacity: 0;
                            transform: translateY(20px);
                            animation: fadeInUp 0.5s ease-out forwards;
                          }

                          .animation-delay-100 { animation-delay: 100ms; }
                          .animation-delay-200 { animation-delay: 200ms; }
                          .animation-delay-300 { animation-delay: 300ms; }
                          .animation-delay-400 { animation-delay: 400ms; }
                          .animation-delay-500 { animation-delay: 500ms; }
                          .prose h1 { color: #2C5F5D; } .prose h2 { color: #2C5F5D; } .prose p { margin-top: 1em; margin-bottom: 1em; }

                          /* Animação para o letreiro de logos */
                          @keyframes infinite-scroll {
                              from { transform: translateX(0); }
                              to { transform: translateX(-100%); }
                          }
                          .animate-infinite-scroll {
                              animation: infinite-scroll 40s linear infinite;
                          }
                          .group:hover .animate-infinite-scroll {
                              animation-play-state: paused;
                          }
                        `}
                    </style>

            {/* === HEADER ATUALIZADO === */}
<header className={cl( 
    'fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out', 
    isScrolled || isMenuOpen ? 'bg-white/95 backdrop-blur-sm shadow-lg' : 'bg-transparent' 
)}>
    <div className="container mx-auto px-6 py-4 flex justify-between items-center">
        <div className="cursor-pointer transition-all duration-300 ease-in-out" onClick={() => handleNavigate('Início')}>
            <img 
                src="https://unrivaled-paprenjak-eb6a2b.netlify.app/LOGO.webp" 
                alt="Dsanzo Pisos Logo" 
                className="h-6 sm:h-8 w-auto transition-all duration-300 ease-in-out" 
            />
        </div>

        <nav className="hidden lg:flex items-center space-x-2">
            {navLinks.map(link => (
                <a key={link} href="#" onClick={(e) => {e.preventDefault(); handleNavigate(link)}}
                  className={cl( 'font-semibold px-4 py-2 rounded-md transition-all duration-300', currentPage === link ? 'text-white bg-[#2C5F5D]' : 'text-[#4A4A4A] hover:bg-gray-100' )}>
                    {link}
                </a>
            ))}
        </nav>
        
                                 <div className="hidden lg:block">
             <CTAButton>Orçamento</CTAButton>
         </div>
        
        {/* === BOTÃO HAMBURGUER MELHORADO === */}
        <div className="lg:hidden">
           <button 
               onClick={() => setIsMenuOpen(!isMenuOpen)} 
               className="relative z-[101] h-12 w-12 flex items-center justify-center rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all duration-300"
               aria-label="Menu de navegação"
           >
              <div className="relative w-6 h-5">
                <span className={cl(
                    "absolute block w-full h-0.5 bg-[#2C5F5D] transition-all duration-300 ease-in-out rounded-full",
                    isMenuOpen ? "top-1/2 -translate-y-1/2 rotate-45" : "top-0"
                )}></span>
                <span className={cl(
                    "absolute block w-full h-0.5 bg-[#2C5F5D] top-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out rounded-full",
                    isMenuOpen ? "opacity-0 scale-0" : "opacity-100 scale-100"
                )}></span>
                <span className={cl(
                    "absolute block w-full h-0.5 bg-[#2C5F5D] transition-all duration-300 ease-in-out rounded-full",
                    isMenuOpen ? "top-1/2 -translate-y-1/2 -rotate-45" : "bottom-0"
                )}></span>
              </div>
           </button>
        </div>
    </div>
</header>

{/* === MENU MOBILE COMPLETAMENTE REDESENHADO === */}
<div className={cl(
    'fixed inset-0 z-[100] transition-all duration-500 lg:hidden',
    isMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
)}>
    {/* Overlay com blur */}
    <div 
        className={cl(
            "absolute inset-0 bg-black/60 backdrop-blur-md transition-all duration-500",
            isMenuOpen ? "opacity-100" : "opacity-0"
        )} 
        onClick={() => setIsMenuOpen(false)}
    ></div>
    
    {/* Painel do Menu */}
    <div className={cl(
        'absolute top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl transition-all duration-500 ease-out',
        isMenuOpen ? 'translate-x-0' : 'translate-x-full'
    )}>
        {/* Header do Menu */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <img 
                src="https://unrivaled-paprenjak-eb6a2b.netlify.app/LOGO.webp" 
                alt="Dsanzo Logo" 
                className="h-8 w-auto"
            />
            <button 
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                aria-label="Fechar menu"
            >
                <X size={24} className="text-gray-600" />
            </button>
        </div>
        
        {/* Navegação Principal */}
        <nav className="flex-1 px-6 py-8">
            <ul className="space-y-2">
                {navLinks.map((link, index) => (
                    <li 
                        key={link} 
                        className={cl(
                            "transform transition-all duration-300 ease-out",
                            isMenuOpen ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0"
                        )}
                        style={{ transitionDelay: `${100 + index * 50}ms` }}
                    >
                        <a 
                            href="#" 
                            onClick={(e) => { e.preventDefault(); handleNavigate(link) }} 
                            className={cl(
                                "flex items-center gap-4 px-4 py-3 rounded-xl font-semibold text-base transition-all duration-200 group",
                                currentPage === link 
                                    ? "bg-[#2C5F5D] text-white shadow-lg" 
                                    : "text-gray-700 hover:bg-gray-50 hover:text-[#2C5F5D]"
                            )}
                        >
                            <div className={cl(
                                "w-2 h-2 rounded-full transition-all duration-200",
                                currentPage === link 
                                    ? "bg-white" 
                                    : "bg-[#D67441] opacity-0 group-hover:opacity-100"
                            )}></div>
                            <span className="font-medium">{link}</span>
                            {currentPage === link && (
                                <ChevronRight size={16} className="ml-auto" />
                            )}
                        </a>
                    </li>
                ))}
            </ul>
        </nav>
        
        {/* Seção de Contato Rápido */}
        <div className="px-6 py-6 border-t border-gray-100 bg-gray-50">
            <div className={cl(
                "transform transition-all duration-500 ease-out",
                isMenuOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            )}
            style={{ transitionDelay: '400ms' }}
            >
                <h3 className="font-bold text-[#2C5F5D] mb-4 text-sm uppercase tracking-wider">
                    Contato Rápido
                </h3>
                
                {/* Botão WhatsApp */}
                <a 
                    href={WHATSAPP_URL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#25D366] text-white rounded-xl hover:bg-[#128C7E] transition-all duration-200 mb-3 group"
                >
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 4.315 1.731 6.086l.099.164-1.157 4.224 4.272-1.129.175.105z"/>
                        </svg>
                    </div>
                    <div className="flex-1">
                        <p className="font-semibold text-sm">WhatsApp</p>
                        <p className="text-xs opacity-90">Fale conosco agora</p>
                    </div>
                    <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform duration-200" />
                </a>
                
                {/* Botão Telefone */}
                <a 
                    href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="flex items-center gap-3 p-3 bg-[#2C5F5D] text-white rounded-xl hover:bg-[#1e4442] transition-all duration-200 group"
                >
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                        <Phone size={16} />
                    </div>
                    <div className="flex-1">
                        <p className="font-semibold text-sm">Telefone</p>
                        <p className="text-xs opacity-90">{CONTACT_INFO.phone}</p>
                    </div>
                    <ArrowRight size={16} className="transform group-hover:translate-x-1 transition-transform duration-200" />
                </a>
            </div>
        </div>
        
        {/* Redes Sociais */}
        <div className="px-6 py-4 border-t border-gray-100">
            <div className={cl(
                "flex justify-center space-x-4 transform transition-all duration-500 ease-out",
                isMenuOpen ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
            )}
            style={{ transitionDelay: '500ms' }}
            >
                <a 
                    href="#" 
                    aria-label="Instagram" 
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-[#E4405F] hover:text-white transition-all duration-200"
                >
                    <Instagram size={18} />
                </a>
                <a 
                    href="#" 
                    aria-label="Facebook" 
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-[#1877F2] hover:text-white transition-all duration-200"
                >
                    <Facebook size={18} />
                </a>
            </div>
        </div>
    </div>
</div>


            <main className="pt-[76px]">
                {renderPage()}
            </main>
            
            <footer className="bg-[#204442] text-gray-300">
                {/* Upper section with links and newsletter */}
                <div className="container mx-auto px-6 lg:px-8 py-16">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                        
                        {/* Column 1: Brand Info & Social */}
                        <div className="flex flex-col">
                            <h4 className="text-2xl font-extrabold font-title text-white mb-4">DSANZO</h4>
                            <p className="text-sm text-gray-400 max-w-sm mb-6">
                                Soluções completas em pisos e revestimentos, com mais de 40 anos de tradição e confiança.
                            </p>
                            <div className="flex space-x-4">
                                <a href="#" aria-label="Instagram" className="bg-white/10 p-2 rounded-full text-gray-300 hover:bg-[#D67441] hover:text-white transition-all duration-300"><Instagram size={20} /></a>
                                <a href="#" aria-label="Facebook" className="bg-white/10 p-2 rounded-full text-gray-300 hover:bg-[#D67441] hover:text-white transition-all duration-300"><Facebook size={20} /></a>
                            </div>
                        </div>

                        {/* Column 2: Quick Links */}
                        <div>
                             <h5 className="font-bold text-white tracking-wider uppercase mb-6">Explore</h5>
                             <ul className="space-y-3 text-sm">
                                {navLinks.map(link => (
                                    <li key={`footer-${link}`}>
                                        <a href="#" onClick={(e) => {e.preventDefault(); handleNavigate(link)}} className="hover:text-white transition-colors duration-300 flex items-center gap-2 group">
                                            <ChevronRight size={14} className="text-[#D67441] opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0 duration-300"/>
                                            <span>{link}</span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        
                        {/* Column 3: Contact Info */}
                        <div>
                            <h5 className="font-bold text-white tracking-wider uppercase mb-6">Contato</h5>
                            <ul className="space-y-4 text-sm">
                                <li className="flex items-start gap-3">
                                    <MapPin size={24} className="text-[#D67441] flex-shrink-0 mt-1"/>
                                    <div>
                                        <p className="font-semibold text-white">Nosso showroom</p>
                                        <p className="text-gray-400">{CONTACT_INFO.address}</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Mail size={16} className="text-[#D67441] flex-shrink-0 mt-1"/>
                                     <div>
                                        <p className="font-semibold text-white">Email</p>
                                        <p className="text-gray-400">contato@grupodsanzo.com.br</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Phone size={16} className="text-[#D67441] flex-shrink-0 mt-1"/>
                                    <div>
                                        <p className="font-semibold text-white">Whatsapp</p>
                                        <p className="text-gray-400">{CONTACT_INFO.phone}</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        {/* Column 4: Newsletter */}
                        <div>
                            <h5 className="font-bold text-white tracking-wider uppercase mb-6">Newsletter</h5>
                            <p className="text-sm mb-4 text-gray-400">Receba novidades e promoções exclusivas em seu e-mail.</p>
                            <form onSubmit={(e) => e.preventDefault()}>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input 
                                        type="email" 
                                        placeholder="Seu melhor e-mail" 
                                        className="w-full bg-white/10 border border-white/20 rounded-md py-2 px-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#D67441] transition-all"
                                    />
                                    <button 
                                        type="submit" 
                                        className="bg-[#D67441] text-white font-bold py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
                                    >
                                        Inscrever
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
                
                {/* Bottom bar */}
                <div className="bg-black/20">
                    <div className="container mx-auto px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center text-xs text-gray-400">
                        <p className="mb-4 sm:mb-0 text-center sm:text-left">
                            © {new Date().getFullYear()} Grupo Dsanzo. Todos os direitos reservados.
                        </p>
                        <div className="flex gap-x-6 gap-y-2 flex-wrap justify-center">
                            {legalLinks.map(link => (
                                <a key={link} href="#" onClick={(e) => {e.preventDefault(); handleNavigate(link)}} className="hover:text-white transition-colors">{link}</a>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>
            
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 bg-[#25D366] text-white w-16 h-16 rounded-full shadow-lg hover:scale-110 transition-transform z-50 flex items-center justify-center">
                 <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.894 11.892-1.99 0-3.903-.52-5.687-1.475L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 4.315 1.731 6.086l.099.164-1.157 4.224 4.272-1.129.175.105z"/></svg>
                 <span className="absolute -top-1 -right-1 flex h-5 w-5">
                    <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500 items-center justify-center text-xs font-bold text-white">1</span>
                </span>
            </a>
        </div>
        } />
        </Routes>
    </Router>
    );
}
